import type { Instance, SnapshotIn, SnapshotOut } from "mobx-state-tree";
import { types, flow, getRoot } from "mobx-state-tree";
import type { ResyncChannelHandle } from "@/lib/realtime/channel";
import {
  DocumentModel,
  DocumentSelectionModel,
  DocumentTagsModel,
  ExpandedClusterModel,
} from "./models";
import * as documentsRepo from "./repository";
import { searchFilteredDocumentContent } from "./contentSearch";
import { COMPLETE_CONTENT_SEARCH } from "./repository/contentSearchStatus";
import type { DownloadDocumentOptions } from "./repository/downloads";
import { keepLatestVersions } from "./versionGrouping";
import type {
  AvailableFilters,
  ContentMatchInfo,
  ContentSearchStatus,
  UploadDateRange,
  FolderDocumentCount,
  TimelineSummaryResult,
  TimelineBucket,
  TimelineGranularity,
  TimelineDateBasis,
  TimelineFilterParams,
  DocumentRowMapped,
  SelectableDocument,
} from "./repository";
import type { SortKey, SortOrder } from "@/domains/documents/utils/documentSorting";
import { COMPLETE_STATES } from "@/domains/documents/utils/documentState";
import { toast } from "@/hooks/use-toast";
import { getTranslationsStatic } from "@/lib/i18n/LanguageContext";
import { eventLogger } from "@/utils/eventLogger";
import { isSignedContainerFile } from "@/domains/documents/constants/fileTypes";
import { bucketContentMatchDocs } from "@/domains/documents/utils/timelineContentMatchBucketing";
import { docsNewestFirst, getDocTimestamp } from "./repository/documentDates";
// From its own module, not the barrel: store tests `vi.mock("./repository")`
// with partial stubs, and an `instanceof` against a missing class throws.
import { TimelineDateBasisUnavailableError } from "./repository/timeline-types";
import { excludeDocsInFolderSubtrees } from "@/domains/documents/utils/folderSelection";
import {
  notifyIfPinned,
  withoutPinnedDocuments,
} from "@/domains/documents/services/pinnedDocuments";

/**
 * How long a cached project/folder slot may be served without going back to the
 * database.
 *
 * The slot is filled once and then only ever mutated by realtime events, so its
 * correctness depends on every event being delivered — and postgres_changes
 * guarantees no such thing (a suspended tab, a dropped socket or a single
 * swallowed handler error loses them permanently, with no replay on re-join).
 * A lineage that gained a version during a gap keeps rendering the superseded
 * row, which is how a four-version document came to report "v2" in the list.
 *
 * The window keeps folder-to-folder navigation instant while bounding how long
 * a missed event can misrepresent the data. Re-entering a folder inside the
 * window still serves the cache; past it the loaders refetch underneath the
 * rows they already show, so nothing flashes.
 */
const PROJECT_DOC_CACHE_TTL_MS = 60_000;

/**
 * The two documents surfaces, which are tabs of one page over one store: the
 * paginated building-wide list (List/Timeline) and the project folder view.
 * Whichever is mounted subscribes, and a resync re-reads that one.
 */
export type DocumentSurface = "list" | "folders" | "timeline";

/**
 * Filter parameters for document queries.
 * Matches repository's ListParams for consistency.
 *
 * Filters are applied at the DATABASE level (before LIMIT/OFFSET),
 * ensuring correct pagination when filtering.
 */
export interface DocumentFilters {
  /** Filter by document states (include only these) */
  states?: string[];
  /** Filter by document states to EXCLUDE */
  excludeStates?: string[];
  /** Filter by project IDs (OR logic - document matches any of these) */
  projectIds?: number[];
  /** Filter by category IDs from document_document_categories M2M (OR logic) */
  categoryIds?: number[];
  /** Filter by design discipline IDs from document_design_disciplines M2M (OR logic) */
  designDisciplineIds?: number[];
  /** Filter by design stage IDs from document_design_stages M2M (OR logic) */
  designStageIds?: number[];
  /** Search in filename using ILIKE */
  searchText?: string;
  /** Filter by folder_id (null to find documents with no folder) */
  folderId?: string | null;
  /** Filter by when the document was added to Franklin. */
  uploadDateRange?: UploadDateRange;
  /** When true, keep only documents carrying the Verified mark. */
  verifiedOnly?: boolean;
  /** Sort field */
  sortKey?: "name" | "created_at" | "modified_at" | "user" | "size";
  /** Sort direction */
  sortOrder?: "asc" | "desc";
}

/**
 * Type for project document cache structure.
 * Map<projectId, Map<folderId, Document[]>>
 * folderId of "root" represents null folder_id (root documents)
 */
type ProjectDocumentCache = Map<string, Map<string, any[]>>;

interface SelectedIdsMap {
  entries(): IterableIterator<[string, boolean]>;
}

function selectedIdsInClickOrder(selectedIds: SelectedIdsMap): string[] {
  return Array.from(selectedIds.entries())
    .filter(([, isSelected]) => isSelected)
    .map(([id]) => id);
}

interface ProjectCacheState {
  projectDocuments: ProjectDocumentCache;
  projectFolderSortMeta: Map<
    string,
    { sortKey?: SortKey; sortOrder?: SortOrder; fetchedAt: number }
  >;
  projectCacheVersion: number;
  projectCacheGeneration: number;
  projectCacheGenerationByProject: Map<string, number>;
  projectFolderRequestSeq: Map<string, number>;
}

/**
 * The generation an in-flight fetch for one project is measured against: that
 * project's own invalidations plus every all-projects one. Both counters only
 * ever increase, so the sum changes exactly when an invalidation concerned
 * these rows — a load of project A is no longer sent back to the network
 * because a picker opened on project B.
 */
function projectCacheGenerationFor(store: ProjectCacheState, projectId: string): number {
  return store.projectCacheGeneration + (store.projectCacheGenerationByProject.get(projectId) ?? 0);
}

/**
 * How many times a fetch will go back to the network because an invalidation
 * landed while it was in flight. Invalidations are user-paced (a picker opens,
 * a bulk action finishes), so one retry is already the rare case; the cap only
 * stops a pathological burst from keeping a folder on the wire forever, and the
 * last attempt is cached whatever the generation says — at worst as stale as
 * the round-trip that produced it.
 */
const MAX_INVALIDATION_REFETCHES = 3;

/**
 * Fetch rows, going back to the network for as long as an invalidation lands
 * while they are in flight.
 *
 * The fetch flows have no cancellation, so a response that left before
 * `invalidateProjectCache` is older than whatever that call was announcing —
 * caching it re-seeds the slot the invalidation just cleared, and the next
 * reader gets pre-invalidation rows. That is how a reopened picker could still
 * offer a superseded version. But dropping the response is no better: the views
 * render the cache, not the returned array, so a folder whose only in-flight
 * load is disowned resolves to "empty folder" with nothing pending to refill
 * it. Only a fresh round-trip satisfies both.
 *
 * `isSuperseded` is the slot's write-sequence check, claimed by loaders and
 * peeks alike — a newer read of the same slot means these rows may not be
 * written, so they go back to the caller unwritten (see
 * projectFolderRequestSeq). It is checked LAST, and only decides the write:
 * rows that predate an invalidation are stale for the caller too, and a peek
 * hands its array straight to the picker whether or not it owns the slot. An
 * upload invalidates and then reloads the folder behind the dialog, so the
 * peek it overtakes is exactly the one that would otherwise return the
 * pre-upload rows — the superseded version, offered for attachment.
 */
function* fetchOutlastingInvalidations(
  store: ProjectCacheState,
  params: documentsRepo.FetchProjectDocumentsParams,
  isSuperseded: () => boolean
): Generator<PromiseLike<any>, { docs: any[]; isSuperseded: boolean }, any> {
  for (let attempt = 0; ; attempt++) {
    const generation = projectCacheGenerationFor(store, params.projectId);
    const docs: any[] = yield documentsRepo.fetchProjectDocuments(params);

    if (
      projectCacheGenerationFor(store, params.projectId) !== generation &&
      attempt < MAX_INVALIDATION_REFETCHES
    ) {
      continue;
    }
    return { docs, isSuperseded: isSuperseded() };
  }
}

/**
 * Write a folder slot. Callers reach here only with rows that postdate the last
 * invalidation — see fetchOutlastingInvalidations.
 */
function commitProjectCacheSlot(
  store: ProjectCacheState,
  projectId: string,
  cacheKey: string,
  docs: any[],
  sort?: { sortKey?: SortKey; sortOrder?: SortOrder }
): void {
  if (!store.projectDocuments.has(projectId)) {
    store.projectDocuments.set(projectId, new Map());
  }
  store.projectDocuments.get(projectId)!.set(cacheKey, docs);
  if (sort) {
    store.projectFolderSortMeta.set(`${projectId}|${cacheKey}`, { ...sort, fetchedAt: Date.now() });
  }
  store.projectCacheVersion += 1;
}

/**
 * Claim a slot on behalf of a patch that has ALREADY been applied to it, so a
 * fetch still on the wire cannot write its pre-patch rows back.
 *
 * The realtime handlers edit these slots in place — a delete strips a row, an
 * upsert moves or replaces one — and are by definition the newest word on the
 * folder. But unlike a fetch they never claimed the write counter, so a peek or
 * loader that left before the event would land afterwards and rewrite the whole
 * slot: the trashed document back in the folder view, and, since the picker
 * renders the peek's array, offered as something already filed there. Bumping
 * past every claim in flight marks those responses superseded, which is what
 * they are. Only slots the patch actually wrote are claimed — a slot with no
 * rows yet is left alone so its first load still fills it.
 */
function claimPatchedSlot(store: ProjectCacheState, projectId: string, cacheKey: string): void {
  const metaKey = `${projectId}|${cacheKey}`;
  store.projectFolderRequestSeq.set(metaKey, (store.projectFolderRequestSeq.get(metaKey) ?? 0) + 1);
}

/**
 * Give back a pending-sort claim whose fetch never landed (see
 * projectFolderPendingSort). Nothing commits that sort now, so a peek reading
 * it would fetch an order no view is showing and write the slot back in it —
 * the exact reordering-behind-the-dialog the claim exists to prevent. Dropped
 * only when it is still ours: a later loader may already have claimed the slot
 * for the sort it is fetching, and that one is still live.
 */
function releasePendingSortClaim(
  pending: Map<string, { sortKey?: SortKey; sortOrder?: SortOrder }>,
  metaKey: string,
  sortKey?: SortKey,
  sortOrder?: SortOrder
): void {
  const claim = pending.get(metaKey);
  if (claim?.sortKey === sortKey && claim?.sortOrder === sortOrder) pending.delete(metaKey);
}

function invalidateProjectCacheState(store: ProjectCacheState, projectId?: string): void {
  // Bump the counter the fetches for these rows measured themselves against:
  // an in-flight fetch captured it before it left, and a mismatch on return is
  // enough to know its rows are older than someone's explicit "this cache is
  // wrong" — which sends it back to the network
  // (fetchOutlastingInvalidations). Scoped invalidations bump only their own
  // project's counter, so nothing else on the wire is disturbed; the counters
  // are never reset, so a later sum can't collide with an earlier one.
  if (projectId) {
    store.projectCacheGenerationByProject.set(
      projectId,
      (store.projectCacheGenerationByProject.get(projectId) ?? 0) + 1
    );
    store.projectDocuments.delete(projectId);
    const prefix = `${projectId}|`;
    for (const key of store.projectFolderSortMeta.keys()) {
      if (key.startsWith(prefix)) store.projectFolderSortMeta.delete(key);
    }
    return;
  }

  // No project named: every fetch in flight is suspect, and the shared term of
  // projectCacheGenerationFor covers all of them at once.
  store.projectCacheGeneration += 1;
  store.projectDocuments.clear();
  store.projectFolderSortMeta.clear();
}

interface FolderDocumentCountState {
  isLoadingFolderCounts: boolean;
  folderDocumentCounts: Map<string, FolderDocumentCount> | null;
  rootDocumentCount: number;
  unreachableDocIds: string[];
}

const fetchFolderDocumentCountsIntoStore = flow(function* fetchFolderDocumentCountsIntoStore(
  store: FolderDocumentCountState,
  projectId: string,
  buildingId: string
) {
  store.isLoadingFolderCounts = true;

  try {
    const result: {
      folders: Map<string, FolderDocumentCount>;
      rootCount: number;
      unreachableDocIds: string[];
    } = yield documentsRepo.fetchFolderDocumentCounts(projectId, buildingId);
    store.folderDocumentCounts = result.folders;
    store.rootDocumentCount = result.rootCount;
    store.unreachableDocIds = result.unreachableDocIds;
    return result;
  } catch (error: unknown) {
    console.error("loadFolderDocumentCounts error", error);
    store.folderDocumentCounts = null;
    store.rootDocumentCount = 0;
    store.unreachableDocIds = [];
    throw error;
  } finally {
    store.isLoadingFolderCounts = false;
  }
});

/** The two fields a cascade walk needs off a cached document row. */
interface CascadeRow {
  id: string | number;
  parent_document_id?: string | number | null;
}

/**
 * Index cached rows by their parent id — the adjacency `collectCascadeDescendants`
 * walks. A row with no parent (null, undefined or "" depending on the surface)
 * is not a child of anything and is skipped.
 */
function indexChildrenByParent(rowGroups: Iterable<CascadeRow>[]): Map<string, Set<string>> {
  const childrenByParent = new Map<string, Set<string>>();
  for (const group of rowGroups) {
    for (const row of group) {
      const parentId = String(row?.parent_document_id ?? "");
      if (!parentId) continue;
      const children = childrenByParent.get(parentId) ?? new Set<string>();
      children.add(String(row.id));
      childrenByParent.set(parentId, children);
    }
  }
  return childrenByParent;
}

/**
 * Every descendant of `seedIds`, transitively, across ALL cached row surfaces.
 * Mirrors the DB trigger that cascades soft-delete via parent_document_id: the
 * server deletes a trashed container's children, so the client has to drop them
 * everywhere too.
 *
 * Takes row groups rather than just the MST map because each surface keeps its
 * own copies — on the timeline a container's child rows can live ONLY in the
 * timeline caches, so a walk over `self.documents` alone never even identifies
 * them and the deleted cards stay on screen (and stay selectable, whereupon the
 * zip builder 404s on them).
 */
function collectCascadeDescendants(rowGroups: Iterable<CascadeRow>[], seedIds: string[]): string[] {
  const childrenByParent = indexChildrenByParent(rowGroups);
  const seen = new Set(seedIds);
  const descendants: string[] = [];
  let frontier = seedIds;
  while (frontier.length > 0) {
    const next: string[] = [];
    for (const parentId of frontier) {
      for (const childId of childrenByParent.get(parentId) ?? []) {
        if (seen.has(childId)) continue;
        seen.add(childId);
        descendants.push(childId);
        next.push(childId);
      }
    }
    frontier = next;
  }
  return descendants;
}

/**
 * Merge content-match docs into a server-side timeline summary.
 *
 * The summary RPC only counts filename matches when searchText is set, so docs
 * that match by content alone are missing. We re-bucket those docs locally at
 * the same granularity Postgres uses (year/month/ISO-week/day) and either
 * increment an existing bucket's count or splice in a new bucket.
 *
 * Caveats (acceptable trade-offs):
 *   - When a content-match doc is ALSO in a filename-match bucket, we slightly
 *     over-count (the summary RPC doesn't return doc IDs, so we can't dedupe
 *     at the bucket layer). The content-match RPC is capped at 200 docs, so
 *     worst-case overcount is bounded.
 *   - If Postgres' session timezone differs from UTC, week/day boundaries can
 *     be off by up to one period for docs near midnight UTC.
 *
 * The returned summary is always a fresh object — the input `raw` is never
 * mutated, so this function is safe to call repeatedly.
 */
function mergeContentMatchesIntoSummary(
  raw: TimelineSummaryResult,
  contentMatchDocs: DocumentRowMapped[],
  granularity: TimelineGranularity,
  basis: TimelineDateBasis
): TimelineSummaryResult {
  if (contentMatchDocs.length === 0) {
    return {
      buckets: raw.buckets.map((b) => ({ ...b })),
      totalDocuments: raw.totalDocuments,
      timeRange: raw.timeRange,
    };
  }

  const contentBuckets = bucketContentMatchDocs(contentMatchDocs, granularity, basis);
  const existingByKey = new Map<string, TimelineBucket>();
  const merged: TimelineBucket[] = raw.buckets.map((b) => {
    const copy: TimelineBucket = { ...b };
    existingByKey.set(b.bucketStart.toISOString(), copy);
    return copy;
  });

  for (const [key, contentBucket] of contentBuckets) {
    const existing = existingByKey.get(key);
    if (existing) {
      // Safe to add without dedup: the search_documents_by_content RPC
      // returns content-only matches (filename overlap is excluded server-side
      // since the WHA-3179 fix), so contentBucket.docs are disjoint from the
      // server's filename-bucket members.
      existing.documentCount += contentBucket.docs.length;
    } else {
      const ids = contentBucket.docs.map((d) => d.id);
      merged.push({
        bucketStart: contentBucket.bucketStart,
        bucketEnd: contentBucket.bucketEnd,
        documentCount: contentBucket.docs.length,
        earliestDocId: Math.min(...ids),
        latestDocId: Math.max(...ids),
      });
    }
  }

  // Server returns buckets DESC by bucketStart; preserve that ordering.
  merged.sort((a, b) => b.bucketStart.getTime() - a.bucketStart.getTime());

  const total = merged.reduce((sum, b) => sum + b.documentCount, 0);
  const timeRange =
    merged.length > 0
      ? {
          start: merged[merged.length - 1].bucketStart,
          end: merged[0].bucketEnd,
        }
      : raw.timeRange;

  return { buckets: merged, totalDocuments: total, timeRange };
}

/**
 * Return a copy of `rows` with one document's filename replaced, or null when
 * that document isn't in the list. Callers use the null to skip the write, so
 * an unrelated rename can't churn a list (and its observers) for nothing.
 */
/**
 * Copy-on-write patch of a row list by document id. Returns `null` when no row
 * matched, so callers can skip the assignment (and the re-render) entirely.
 */
function patchRows(
  rows: readonly DocumentRowMapped[],
  patchById: Map<string, Partial<DocumentRowMapped>>
): DocumentRowMapped[] | null {
  let next: DocumentRowMapped[] | null = null;
  for (let i = 0; i < rows.length; i++) {
    const patch = patchById.get(String(rows[i].id));
    if (!patch) continue;
    if (!next) next = rows.slice();
    next[i] = { ...next[i], ...patch };
  }
  return next;
}

function renameInRows(
  rows: readonly DocumentRowMapped[],
  docId: string,
  newFilename: string
): DocumentRowMapped[] | null {
  return patchRows(rows, new Map([[docId, { filename: newFilename }]]));
}

/**
 * Merge server-fetched individual docs (filename matches) with content-match docs.
 * Content-only extras are appended and the combined list is sorted DESC by timestamp
 * to match the server's natural ordering.
 */
function mergeIndividualDocs(
  serverDocs: DocumentRowMapped[],
  contentMatches: DocumentRowMapped[],
  basis: TimelineDateBasis
): DocumentRowMapped[] {
  if (contentMatches.length === 0) return serverDocs.slice();
  const seen = new Set(serverDocs.map((d) => d.id));
  const extras = contentMatches.filter((d) => !seen.has(d.id));
  if (extras.length === 0) return serverDocs.slice();
  return [...serverDocs, ...extras].sort(docsNewestFirst(basis));
}

/**
 * An expanded cluster's rows, its count, and the ids of the content-only
 * matches the CLIENT added to them — the only ids whose count a later page may
 * take back (see `appendPageIntoWindow`).
 */
interface WindowMerge {
  documents: DocumentRowMapped[];
  totalCount: number;
  splicedIds: string[];
}

/**
 * The cluster-window equivalent of `mergeIndividualDocs`: the filename RPC does
 * not know about content matches, so an expanded cluster splices in the ones
 * whose timestamp falls inside its window and counts them into the total.
 *
 * Every write of a cluster's rows goes through this — including a stale-cursor
 * restart, which replaces the rows wholesale and would otherwise drop the
 * spliced-in hits and shrink the visible count back to the server's.
 */
function mergeContentMatchesIntoWindow(
  contentMatches: DocumentRowMapped[],
  result: { documents: DocumentRowMapped[]; totalCount: number },
  startTime: Date,
  endTime: Date,
  basis: TimelineDateBasis
): WindowMerge {
  const filenameIds = new Set(result.documents.map((d) => d.id));
  const contentInWindow = contentMatches.filter((d) => {
    if (filenameIds.has(d.id)) return false;
    const ts = getDocTimestamp(d, basis);
    // Half-open, exactly as `get_documents_in_time_window` filters
    // (`<basis key> >= p_start_time AND < p_end_time`) and as
    // `date_trunc(...) + interval` cuts the summary's buckets. Adjacent
    // clusters share a boundary instant, so an inclusive end splices a
    // content-only hit landing on one into BOTH of them — rendered twice and
    // counted twice.
    return ts >= startTime && ts < endTime;
  });
  if (contentInWindow.length === 0) {
    return { documents: result.documents, totalCount: result.totalCount, splicedIds: [] };
  }
  return {
    documents: [...result.documents, ...contentInWindow].sort(docsNewestFirst(basis)),
    totalCount: result.totalCount + contentInWindow.length,
    // Only these — the ids the client added on top of the server's total. A
    // content match the server itself returned was filtered out above and was
    // never double-counted, so it must not be subtracted for later either.
    splicedIds: contentInWindow.map((d) => String(d.id)),
  };
}

/**
 * Fold a later page into the rows an expanded cluster already holds.
 *
 * Page one is not the server's page one: `mergeContentMatchesIntoWindow`
 * spliced the window's content-only hits into it and sorted the result. So a
 * later page cannot simply be pushed onto the end — a document that matches
 * BOTH the content search and the filename query comes back from the RPC on
 * this page and is already on screen from the splice (it would render twice),
 * and a spliced row older than this page's rows would sit above them, so dates
 * read out of order inside the cluster. Dedupe by id and re-sort by timestamp.
 *
 * The count only comes down for a duplicate THIS WINDOW spliced in — one the
 * splice counted as a content-only extra on top of a server total that already
 * included it. Two duplicates must not: a row the server itself repeated across
 * two pages (its ordering shifted under the cursor) was counted once, and so was
 * a content match the server already returned on page one, which the splice
 * skipped as a filename hit. Both would drift the total low. So the set carried
 * here is `splicedIds` — what `mergeContentMatchesIntoWindow` actually added —
 * not every content match in the search.
 *
 * A settled id leaves the set: the server has now paged the row in itself, so a
 * third page repeating it is an ordinary server duplicate.
 */
function appendPageIntoWindow(
  existing: readonly DocumentRowMapped[],
  page: DocumentRowMapped[],
  totalCount: number,
  splicedIds: readonly string[],
  basis: TimelineDateBasis
): WindowMerge {
  const seen = new Set(existing.map((d) => d.id));
  const fresh = page.filter((d) => !seen.has(d.id));
  const stillSpliced = new Set(splicedIds);
  let settled = 0;
  for (const doc of page) {
    if (seen.has(doc.id) && stillSpliced.delete(String(doc.id))) settled++;
  }
  return {
    documents: [...existing, ...fresh].sort(docsNewestFirst(basis)),
    totalCount: Math.max(0, totalCount - settled),
    splicedIds: [...stillSpliced],
  };
}

/**
 * Rebuild every merged timeline field from the raw ones.
 *
 * The timeline's visible state is a merge of two independently-fetched inputs:
 * the server summary / individual docs, and the content-match docs. Three flows
 * write those raw inputs, and each used to recompute the merge its own way —
 * so whichever landed last silently dropped the other's contribution (a summary
 * arriving after a content search wiped the content matches out of the buckets,
 * and vice versa). Per-flow sequence guards cannot fix that: flow A's guard
 * says nothing about flow B.
 *
 * Instead each flow writes ONLY its own raw fields and calls this. Whatever the
 * arrival order, the merged view reflects the newest raw of each kind — which
 * is what `timelineSummaryRaw` was introduced for in the first place.
 */
function recomputeTimelineMerges(self: {
  timelineSummaryRaw: TimelineSummaryResult | null;
  timelineSummary: TimelineSummaryResult | null;
  timelineGranularity: TimelineGranularity;
  timelineDateBasis: TimelineDateBasis;
  timelineContentMatchDocs: DocumentRowMapped[];
  timelineIndividualServerDocs: DocumentRowMapped[];
  timelineIndividualServerTotalCount: number;
  timelineIndividualDocs: DocumentRowMapped[];
  timelineIndividualTotalCount: number;
}): void {
  if (self.timelineSummaryRaw) {
    self.timelineSummary = mergeContentMatchesIntoSummary(
      self.timelineSummaryRaw,
      self.timelineContentMatchDocs,
      self.timelineGranularity,
      self.timelineDateBasis
    );
  }

  self.timelineIndividualDocs = mergeIndividualDocs(
    self.timelineIndividualServerDocs,
    self.timelineContentMatchDocs,
    self.timelineDateBasis
  );

  // The server's own total, plus the content-only extras it doesn't know about.
  // `timelineIndividualServerDocs` is only the loaded page, so counting it here
  // would under-report the total as soon as the list is paginated.
  const seenServer = new Set(self.timelineIndividualServerDocs.map((d) => d.id));
  const extras = self.timelineContentMatchDocs.filter((d) => !seenServer.has(d.id)).length;
  self.timelineIndividualTotalCount = self.timelineIndividualServerTotalCount + extras;
}

/**
 * Union several document-row collections into one deduped `SelectableDocument`
 * list (first occurrence of each id wins). Backs `allSelectableDocuments` /
 * `selectedDocuments` so selection is resolved to objects in exactly one place.
 */
function dedupeSelectable(groups: Iterable<SelectableDocument>[]): SelectableDocument[] {
  const byId = new Map<string, SelectableDocument>();
  for (const group of groups) {
    for (const doc of group) {
      if (!doc || doc.id === null || doc.id === undefined) continue;
      const id = String(doc.id);
      if (!byId.has(id)) byId.set(id, doc);
    }
  }
  return Array.from(byId.values());
}

/**
 * Flatten the project-scoped row cache (`Map<projectId, Map<folderId, rows>>`)
 * into row groups for the selection union. `/projects` rows are written here by
 * `loadProjectRootDocuments` / folder loads and never enter the MST `documents`
 * map, so without them a doc selected in folder A is unresolvable the moment
 * the user navigates to folder B (and the folder-checkbox cascade, which
 * selects a whole subtree, resolves nothing at all).
 */
function projectRowGroups(cache: ProjectDocumentCache): SelectableDocument[][] {
  const groups: SelectableDocument[][] = [];
  for (const perFolder of cache.values()) {
    for (const rows of perFolder.values()) groups.push(rows);
  }
  return groups;
}

/** Drop rows for the given ids from a timeline row cache; null when none matched. */
function removeRows(
  rows: readonly DocumentRowMapped[],
  removedIds: ReadonlySet<string>
): DocumentRowMapped[] | null {
  const next = rows.filter((doc) => !removedIds.has(String(doc.id)));
  return next.length === rows.length ? null : next;
}

/** True when a bulk-trash call has neither documents nor folders to act on. */
function hasNothingToTrash(selectedIds: string[], folderIds?: string[]): boolean {
  return selectedIds.length === 0 && (!folderIds || folderIds.length === 0);
}

/** Toast for a bulk-trash call made with an empty selection. */
function warnNothingSelectedToTrash() {
  toast({
    title: "No selection",
    description: "Please select documents to move to trash",
    variant: "destructive",
  });
}

/** The slice of the MST root a bulk mutation needs to identify its actor. */
interface BulkActorRoot {
  userStore?: { userProfile?: { id?: string | number | null } | null } | null;
  organizationStore?: { currentOrganization?: { id?: string | null } | null } | null;
  authStore?: { currentOrganizationId?: string | null } | null;
}

/**
 * Resolve the acting user + org for a bulk mutation from the MST root.
 * Throws (after toasting) when the user profile is missing — a bulk write
 * without an actor fails RLS anyway.
 */
function resolveBulkActor(root: BulkActorRoot): { userId: string; orgId: string | null } {
  const userId = root?.userStore?.userProfile?.id;
  if (!userId) {
    toast({
      title: "Error",
      description: "User authentication required",
      variant: "destructive",
    });
    throw new Error("User profile missing");
  }
  const orgId =
    root?.organizationStore?.currentOrganization?.id ||
    root?.authStore?.currentOrganizationId ||
    null;
  return { userId: String(userId), orgId };
}

/** The store slice a building switch resets (see `applyBuildingScope`). */
interface BuildingScopeState {
  documents: { clear(): void };
  selection: { selectedIds: { clear(): void } };
  contentMatchDocIds: Set<string>;
  contentMatchInfoById: Map<string, ContentMatchInfo>;
  contentOnlyMatchCount: number;
  searchStatus: ContentSearchStatus;
  totalCount: number;
  currentOffset: number;
  currentPage: number;
  currentBuildingId: string | null;
  currentProjectId: string | null;
  currentFolderId: string | null;
}

/**
 * Point the store at the building the user is looking at, dropping everything
 * that belonged to the previous one. Must be called from an action.
 *
 * The rows go so the old building's documents can't flash into the new
 * building's view; the SELECTION goes because a bulk action now recovers ids no
 * surface renders via an id-only server lookup (see
 * `resolveSelectionForAction`). Left alive, three documents checked in building
 * A would be recovered — same org, so RLS returns them — and trashed, zipped or
 * sent for signature alongside a document picked in building B.
 *
 * Every documents surface calls this (the page, for the Folders tab; and
 * `loadDocuments`, for List/Timeline), so no tab can keep a selection across a
 * building switch.
 */
function applyBuildingScope(store: BuildingScopeState, buildingId: string) {
  if (!buildingId || store.currentBuildingId === buildingId) return;
  if (store.currentBuildingId) {
    store.documents.clear();
    store.selection.selectedIds.clear();
    store.contentMatchDocIds = new Set();
    store.contentMatchInfoById = new Map();
    store.contentOnlyMatchCount = 0;
    store.searchStatus = COMPLETE_CONTENT_SEARCH;
    store.totalCount = 0;
    store.currentOffset = 0;
    store.currentPage = 1;
    // The folder view's cache key goes too: a project belongs to exactly one
    // building, so carrying it into the next one aims every reader of it —
    // above all a resync — at documents from the building the user just left.
    // The view sets it again from its own load.
    store.currentProjectId = null;
    store.currentFolderId = null;
  }
  store.currentBuildingId = buildingId;
}

/** The store slice a project-scope change resets (see `applyProjectScope`). */
interface ProjectScopeState {
  selection: { selectedIds: { clear(): void } };
  currentProjectScopeId: string | null;
}

/**
 * Narrow the store to the active building project scope (null = the whole
 * building), dropping the selection when that scope moves. Must be called from
 * an action.
 *
 * Same reasoning as the building switch above, one level down: the recovery in
 * `resolveSelectionForAction` refuses another BUILDING's rows, but a document
 * picked under project A is a perfectly valid row of this building, so it would
 * be recovered and signed, trashed or zipped from project B — from a tab where
 * the user cannot see it is checked. Only the selection goes: the rows are
 * reloaded under the new scope anyway.
 */
function applyProjectScope(store: ProjectScopeState, projectId: string | null) {
  if (store.currentProjectScopeId === projectId) return;
  store.currentProjectScopeId = projectId;
  store.selection.selectedIds.clear();
}

/** The store slice `resolveSelectionForAction` reads (also callable from a flow). */
interface SelectionResolveTarget {
  selectedDocumentIds: string[];
  allSelectableDocuments(extraRows?: SelectableDocument[]): SelectableDocument[];
  /** Building the surfaces are showing; recovered rows outside it are refused. */
  currentBuildingId: string | null;
}

/** The current selection resolved for a bulk action, in click order. */
interface ResolvedSelection {
  /** Rows the action should act on itself, click order preserved. */
  docs: SelectableDocument[];
  /** Selected ids no surface and no server round-trip could resolve. */
  unresolvedCount: number;
}

/**
 * Resolve the selection to full rows for a bulk action, recovering ids no
 * rendered surface holds.
 *
 * A selection outlives the row cache that rendered it: `loadDocuments` /
 * `goToPage` clear `self.documents` and repopulate with the new page, and the
 * timeline caches come and go with zoom, collapse and filter changes — none of
 * them clears the selection. Those documents are still real, so a bulk action
 * must recover them instead of skipping them: skipping is what silently trashed
 * only 1 of a 2-document selection made across two pages.
 *
 * `folderIds` are the folders the SAME action acts on. Their contents are
 * handled server-side (the trash RPC's folder cascade, the zip builder's
 * subtree query) under that side's own visibility rules, so ids inside them are
 * left out here rather than recovered and acted on individually — recovering
 * them would pull rows those rules exclude (hidden docs, DWG child PDFs) into
 * the action. Every OTHER unresolved id is recovered, including one picked on a
 * page the user has since left: short-circuiting the whole recovery because
 * some folder is checked is what silently dropped it from the zip.
 *
 * The recovery query matches on id alone, so it is fenced twice. By the store's
 * current building: a recovered row from another building is refused and
 * counted unresolved rather than acted on (the selection is already cleared on
 * a building switch in `applyBuildingScope` — this is the second gate, because
 * trashing or signing another building's documents is not recoverable). And by
 * parentage: a recovered row whose PARENT is selected too is a generated child
 * (a DWG's child PDF, an extracted container member) that no list renders, so
 * it is dropped rather than acted on in its own right — the folder cascade
 * selects the whole folder's rows, children included, and signing pulls in
 * whatever it is handed.
 *
 * Both lookups are round-trips the user keeps clicking through, so the
 * selection is re-read afterwards and only ids selected BOTH at request time
 * and still now are acted on: a row unchecked while the resolve is in flight
 * must not be trashed, and one checked meanwhile belongs to the next action.
 *
 * Throws if the recovery query fails — an empty result must not be read as
 * "these documents are gone" when the truth is "we never found out".
 */
async function resolveSelectionForAction(
  store: SelectionResolveTarget,
  extraRows: SelectableDocument[],
  folderIds: string[]
): Promise<ResolvedSelection> {
  const requestedIds = store.selectedDocumentIds;
  const requested = new Set(requestedIds);
  const byId = new Map<string, SelectableDocument>(
    store.allSelectableDocuments(extraRows).map((doc) => [String(doc.id), doc])
  );

  let missing = requestedIds.filter((id) => !byId.has(id));
  if (missing.length > 0 && folderIds.length > 0) {
    missing = await excludeDocsInFolderSubtrees(missing, folderIds);
  }
  const missingIds = new Set(missing);
  const numericMissing = missing.map(Number).filter((id) => Number.isFinite(id));
  const recovered =
    numericMissing.length > 0
      ? await documentsRepo.fetchDocumentsByIds(numericMissing, { throwOnError: true })
      : [];
  const scopeBuildingId = store.currentBuildingId;
  // Children whose parent is selected too: covered by the action on the parent
  // (the trash RPC cascades, the zip carries the container), never a document
  // of their own here — and NOT reported unresolved, since nothing is lost.
  const coveredByParent = new Set<string>();
  recovered.forEach((doc) => {
    if (scopeBuildingId && String(doc.building_id) !== scopeBuildingId) return;
    const parentId = doc.parent_document_id != null ? String(doc.parent_document_id) : null;
    if (parentId && requested.has(parentId)) {
      coveredByParent.add(String(doc.id));
      return;
    }
    byId.set(String(doc.id), doc);
  });

  const stillSelected = new Set(store.selectedDocumentIds);
  const actedIds = requestedIds.filter((id) => stillSelected.has(id));

  return {
    docs: actedIds
      .map((id) => byId.get(id))
      .filter((doc): doc is SelectableDocument => doc !== undefined),
    // Whatever is still missing after the round-trip is gone or unreadable
    // under RLS; ids left to a folder cascade were never missing.
    unresolvedCount: actedIds.filter(
      (id) => missingIds.has(id) && !byId.has(id) && !coveredByParent.has(id)
    ).length,
  };
}

/**
 * Toast for a selection lookup that failed. Distinct from "these documents no
 * longer exist": nothing was acted on and retrying is worth it.
 */
function reportSelectionLookupFailure(e: unknown) {
  console.error("selection lookup failed", e);
  toast({
    title: getTranslationsStatic().common.error,
    description: "Could not verify the selected documents. Nothing was changed — please try again.",
    variant: "destructive",
  });
}

/** Toast for selected ids that no longer resolve to a document. */
function warnUnresolvedSelection(unresolvedCount: number) {
  toast({
    title: getTranslationsStatic().common.warning,
    description: `${unresolvedCount} selected document(s) could not be found and will be skipped`,
    variant: "destructive",
  });
}

/**
 * The rows a bulk trash acts on. `resolveSelectionForAction` has already tried
 * the server, so anything it could not resolve is a document that no longer
 * exists or that RLS won't return — trashing it would be a no-op anyway, and it
 * is reported here (never for ids the folder cascade covers: those are trashed,
 * not skipped).
 */
function trashableIds(resolved: ResolvedSelection): string[] {
  if (resolved.unresolvedCount > 0) warnUnresolvedSelection(resolved.unresolvedCount);
  return resolved.docs.map((doc) => String(doc.id));
}

/** The timeline row caches a trash has to patch (see `removeTrashedRowsFromTimeline`). */
interface TimelineRowCaches {
  expandedClusters: {
    values(): Iterable<{
      documents: DocumentRowMapped[];
      removeDocuments(ids: ReadonlySet<string>): void;
    }>;
  };
  timelineIndividualDocs: DocumentRowMapped[];
  timelineIndividualServerDocs: DocumentRowMapped[];
  timelineContentMatchDocs: DocumentRowMapped[];
  timelineContentMatchIds: Set<string>;
  timelineContentMatchInfoById: Map<string, ContentMatchInfo>;
  timelineIndividualTotalCount: number;
}

/**
 * Every cached row surface a delete has to walk for cascade children, and then
 * patch: the MST map plus the timeline's own copies. Feeds
 * `collectCascadeDescendants`, whose result goes to BOTH the map deletes and
 * `removeTrashedRowsFromTimeline` — patching only what the map knows is what
 * left cascade-deleted children rendered on the timeline.
 */
function cascadeRowGroups(
  store: TimelineRowCaches & { documents: { values(): Iterable<CascadeRow> } }
): Iterable<CascadeRow>[] {
  return [
    store.documents.values(),
    store.timelineIndividualDocs,
    store.timelineIndividualServerDocs,
    store.timelineContentMatchDocs,
    ...Array.from(store.expandedClusters.values(), (cluster) => cluster.documents),
  ];
}

/**
 * Drop trashed rows from every timeline cache, mirroring `applyRename`'s
 * fan-out: each timeline view keeps its own copy of a row, so patching only the
 * MST `documents` map leaves the deleted cards on screen (and re-selectable,
 * whereupon the zip builder 404s on them).
 */
function removeTrashedRowsFromTimeline(store: TimelineRowCaches, trashedIds: string[]) {
  if (trashedIds.length === 0) return;
  const gone = new Set(trashedIds);

  for (const cluster of store.expandedClusters.values()) {
    cluster.removeDocuments(gone);
  }

  const nextIndividual = removeRows(store.timelineIndividualDocs, gone);
  if (nextIndividual) {
    store.timelineIndividualTotalCount = Math.max(
      0,
      store.timelineIndividualTotalCount -
        (store.timelineIndividualDocs.length - nextIndividual.length)
    );
    store.timelineIndividualDocs = nextIndividual;
  }

  const nextServer = removeRows(store.timelineIndividualServerDocs, gone);
  if (nextServer) store.timelineIndividualServerDocs = nextServer;

  const nextContentMatches = removeRows(store.timelineContentMatchDocs, gone);
  if (nextContentMatches) {
    store.timelineContentMatchDocs = nextContentMatches;
    // Replaced, not mutated: these are volatile fields, so MST holds them
    // shallowly — the Set/Map themselves are plain objects and deleting in
    // place notifies nobody, leaving the match badge/snippet on a trashed row.
    store.timelineContentMatchIds = new Set(
      Array.from(store.timelineContentMatchIds).filter((id) => !gone.has(id))
    );
    const nextInfoById = new Map(store.timelineContentMatchInfoById);
    gone.forEach((id) => nextInfoById.delete(id));
    store.timelineContentMatchInfoById = nextInfoById;
  }
}

/** Toast + rethrow for a bulk trash that failed mid-flight. */
function reportBulkTrashFailure(e: unknown): never {
  console.error("bulkMoveToTrash error", e);
  const errorMsg = e instanceof Error ? e.message : String(e);
  toast({
    title: "Error",
    description: `Failed to move documents to trash: ${errorMsg}`,
    variant: "destructive",
  });
  throw new Error(errorMsg);
}

export const DocumentStore = types
  .model("DocumentStore", {
    documents: types.map(DocumentModel),
    selection: types.optional(DocumentSelectionModel, { selectedIds: {} }),
    tags: types.optional(DocumentTagsModel, {
      projects: {},
      folders: {},
      customTags: [],
    }),
    isLoading: types.optional(types.boolean, false),
    error: types.maybeNull(types.string),
    currentOffset: types.optional(types.number, 0),
    pageSize: types.optional(types.number, 50),
    totalCount: types.optional(types.number, 0),
    /** Count of content-search-only matches appended outside filename pagination */
    contentOnlyMatchCount: types.optional(types.number, 0),
    currentPage: types.optional(types.number, 1),
    isLoadingMore: types.optional(types.boolean, false),
    /** Stored filter parameters for pagination continuity */
    currentFilters: types.optional(types.frozen<DocumentFilters>(), {}),
    /** Currently selected project ID for project document navigation */
    currentProjectId: types.maybeNull(types.string),
    /** Currently selected folder ID for project document navigation (null = root) */
    currentFolderId: types.maybeNull(types.string),
    /** Loading state for project document fetches */
    isLoadingProjectDocs: types.optional(types.boolean, false),
    /** Cache version counter - incremented when projectDocuments cache updates to trigger reactivity */
    projectCacheVersion: types.optional(types.number, 0),
    /**
     * Total document count for the building (unfiltered) - from efficient COUNT query.
     * `null` until a load succeeds, and back to `null` when one fails: a
     * denominator is only honest once counted, and `0` is a real count, never a
     * "not loaded yet" sentinel.
     */
    buildingDocumentCount: types.maybeNull(types.number),
    /**
     * Expanded clusters with pagination state (MST observable map).
     * Key: "startTimeISO|endTimeISO"
     */
    expandedClusters: types.map(ExpandedClusterModel),
    /** Count of documents currently processing (not in terminal state) - updated via WebSocket */
    processingCount: types.optional(types.number, 0),
  })
  .volatile(() => ({
    channel: null as ResyncChannelHandle | null,
    /**
     * `visibilitychange` listener installed alongside the realtime channel and
     * removed with it. A backgrounded tab's socket can be suspended without
     * ever reporting an error, so returning to it is its own resync trigger.
     */
    visibilityHandler: null as (() => void) | null,
    /**
     * When the building-wide list last came from the database. The project
     * views keep the same stamp per cache slot in projectFolderSortMeta; this
     * is the equivalent for the paginated /documents list, which has no slot.
     */
    documentsFetchedAt: 0,
    /**
     * When the Timeline's server-side aggregation last came from the database.
     * The Timeline needs its own stamp: `documentsFetchedAt` is written only by
     * loadDocuments / goToPage, which never run on that tab, so gating the
     * timeline resync on it left the TTL permanently expired and re-ran the
     * whole summary + content search on every realtime event.
     */
    timelineFetchedAt: 0,
    /**
     * Which documents surface currently owns the realtime subscription, and so
     * which one a resync must re-read. Set by `subscribe`, whose caller is by
     * definition the mounted view.
     *
     * It cannot be inferred from `currentProjectId`: only the folder loaders
     * ever set that prop and nothing on the documents page clears it, so after
     * a single visit to the Folders tab it stays set for the rest of the
     * session — and List/Timeline resyncs would reload an unmounted folder slot
     * while the page the user is actually looking at is never re-read.
     */
    activeSurface: "list" as DocumentSurface,
    /**
     * The Timeline's re-read, registered by the view that owns its filter
     * and zoom state. Those live entirely in `useServerTimelineZoom`, and
     * copying them into the store would make a second source of truth for
     * the filter set — so the store holds the callback, not the filters.
     */
    timelineRevalidate: null as (() => void) | null,
    /** Filter to include only these states (used for completed docs view) */
    stateFilter: null as string[] | null,
    /** Filter to exclude these states (used for processing docs view) */
    excludeStateFilter: null as string[] | null,
    /** Current building ID for state filter tracking */
    currentBuildingId: null as string | null,
    /**
     * Project the active scope narrows every documents tab to (null = the whole
     * building). Distinct from `currentProjectId`, which is the folder view's
     * own cache key — this one only tracks what the user is scoped to, so the
     * selection can be dropped when they leave it (see `applyProjectScope`).
     */
    currentProjectScopeId: null as string | null,
    /**
     * Project document cache.
     * Structure: Map<projectId, Map<folderId, Document[]>>
     * folderId of "root" represents null folder_id (root documents)
     */
    projectDocuments: new Map() as ProjectDocumentCache,
    /**
     * Available filter values for the current building.
     * Fetched via fetchAvailableFilters() for efficient dropdown population.
     */
    availableFilters: null as AvailableFilters | null,
    /** Loading state for available filters */
    isLoadingFilters: false,
    /** True when the newest loadAvailableFilters attempt failed. Lets the
     *  Filters popover show an error + retry instead of four empty dimensions
     *  (there is no documents-derived fallback at the page level). */
    filtersLoadFailed: false,
    /** Monotonic seq for loadAvailableFilters — only the newest in-flight
     *  request may write the shared availableFilters/isLoadingFilters fields,
     *  so a slow response for scope A can't clobber scope B's values. */
    filtersRequestSeq: 0,
    /** Monotonic seq for loadDocumentCount, same reason as filtersRequestSeq:
     *  the count is driven by realtime as well as by scope changes, so a
     *  refresh fired for the previous project can resolve after the switch and
     *  put the wrong scope's number on screen. */
    countRequestSeq: 0,
    /** Monotonic seq for loadTags, same reason again — the tag maps are shared
     *  by every chip and breadcrumb, and a stale scope's response replacing
     *  them shows another project's folders. */
    tagsRequestSeq: 0,
    /** building:project scope the current availableFilters belong to. A load
     *  for a different scope clears the values up front (see
     *  loadAvailableFilters) — stale-while-revalidate is only safe within one
     *  scope. */
    filtersScopeKey: null as string | null,
    /** building:project scope the current buildingDocumentCount belongs to. A
     *  load for a different scope drops the count up front (see
     *  loadDocumentCount) — a denominator from the previous scope is simply
     *  wrong, not stale. */
    countScopeKey: null as string | null,
    /**
     * Folder document counts for the current project.
     * Map of folderId -> count info (direct and recursive totals).
     */
    folderDocumentCounts: null as Map<string, FolderDocumentCount> | null,
    /** Root document count (documents not in any folder) for current project */
    rootDocumentCount: 0,
    /**
     * Documents of the current project whose `folder_id` names no live folder
     * of it — a folder in another project, or a soft-deleted one. The Folders
     * tab has no node to render them under, so it lists them at the project
     * root instead of dropping them (see fetchFolderDocumentCounts).
     */
    unreachableDocIds: [] as string[],
    /** Loading state for folder counts */
    isLoadingFolderCounts: false,
    /**
     * Monotonic seq for every flow that replaces `documents` — loadDocuments and
     * goToPage bump it, loadMoreDocuments only observes it.
     *
     * One counter for both replacers because they write the same fields
     * (documents, totalCount, currentOffset, currentPage); two would let a page
     * change and a filter change each believe it was newest. The asymmetry for
     * loadMoreDocuments is deliberate the other way: a replacement must
     * invalidate a pending append, but an append must never invalidate a
     * pending replacement.
     *
     * Search is what makes this load-bearing. Typing "ab" then "abc" leaves two
     * three-hop fetches in flight, and without the guard the slower one calls
     * documents.clear() last and paints the wrong term's rows — while the FIRST
     * to settle clears isLoading, so the spinner vanishes mid-search.
     */
    documentsRequestSeq: 0,
    /** Set of document IDs currently counted as processing (for WebSocket deduplication) */
    processingDocIds: new Set<string>(),
    /** Set of document IDs that matched via content search (not filename) */
    contentMatchDocIds: new Set<string>(),
    /** Per-document content-match metadata for the chip tooltip ("Found on page 3 (+4 more)").
     *  Populated by loadDocuments / loadTimelineContentMatches and by external sync from
     *  /projects (ProjectScopedFolderView). Empty when no search is active. */
    contentMatchInfoById: new Map<string, ContentMatchInfo>(),
    /** How complete the List's text search was (drives the search notice). */
    searchStatus: { isPartial: false, contentUnavailable: false } as ContentSearchStatus,

    // ========================================================================
    // Timeline State (Server-Side Aggregation for 6000+ documents)
    // ========================================================================

    /** Timeline summary with bucketed counts (no individual docs until expanded) */
    timelineSummary: null as TimelineSummaryResult | null,
    /** Pristine server-side summary kept separately so content-match merge stays idempotent. */
    timelineSummaryRaw: null as TimelineSummaryResult | null,
    /** Current granularity for timeline bucketing (year/month/week/day) */
    timelineGranularity: "month" as TimelineGranularity,
    /**
     * The date basis the timeline's rows were fetched on — stamped by the
     * summary and individual-docs loads from their filters, and read by every
     * client-side merge, splice and sort, so they place rows on the same date
     * the server bucketed and paged them by.
     */
    timelineDateBasis: "file" as TimelineDateBasis,
    /**
     * The database rejected an upload-basis read: it predates `p_date_basis`
     * (franklin-data-pipeline 20261016102236). The Timeline falls back to the
     * file basis and disables the option for this session.
     */
    timelineDateBasisUnavailable: false,
    /** Loading state for timeline operations */
    timelineLoading: false,
    /** Error from timeline operations */
    timelineError: null as string | null,
    /** Full doc rows that match by content (filename or content) for current timeline searchText. */
    timelineContentMatchDocs: [] as DocumentRowMapped[],
    /** Set of doc IDs (string) that are content matches — for O(1) badge lookup. */
    timelineContentMatchIds: new Set<string>(),
    /** Per-doc match metadata for the timeline view's Content chip tooltip. */
    timelineContentMatchInfoById: new Map<string, ContentMatchInfo>(),
    /** searchText that produced timelineContentMatchDocs — used to skip refetch on equal search. */
    timelineContentMatchSearchText: null as string | null,
    /** Every filter the timeline content search ran under — a change re-runs it. */
    timelineContentMatchFilterKey: null as string | null,
    /** How complete the timeline's text search was (drives the search notice). */
    timelineSearchStatus: { isPartial: false, contentUnavailable: false } as ContentSearchStatus,
    /** Monotonic seq for loadTimelineSummary — see documentsRequestSeq. */
    timelineSummaryRequestSeq: 0,
    /** Monotonic seq for loadTimelineContentMatches. */
    timelineContentRequestSeq: 0,
    /** Monotonic seq for loadTimelineIndividualDocs. Bumped by anything that
     *  REPLACES the list — a reset, or a load-more the repository restarted from
     *  page one; a load-more that merely appends observes it instead. */
    timelineIndividualRequestSeq: 0,

    // ========================================================================
    // Timeline Individual Docs Mode (Max Zoom)
    // ========================================================================

    /** Individual documents for max zoom mode (not using clusters) — merged display list */
    timelineIndividualDocs: [] as DocumentRowMapped[],
    /** Server-only individual docs (filename matches), used to re-merge when content matches load. */
    timelineIndividualServerDocs: [] as DocumentRowMapped[],
    /** The server's total for the individual-docs query, independent of how
     *  many pages have been loaded. `timelineIndividualServerDocs` is only the
     *  loaded prefix, so the merged total is derived from this. */
    timelineIndividualServerTotalCount: 0,
    /** Total count of documents in the timeline (server total + content-only extras) */
    timelineIndividualTotalCount: 0,
    /** Whether there are more documents to load */
    timelineIndividualHasMore: false,
    /**
     * True when paging gave up short of the end: a stale cursor was restarted
     * and the restart could not page back to the depth we held. `hasMore` is
     * false because the cursor is unusable, not because the list ran out, so
     * the view offers a retry and the retry re-reads from the start.
     */
    timelineIndividualTruncated: false,
    /** Cursor for pagination (timestamp + id of last fetched doc) */
    timelineIndividualCursor: null as { timestamp: Date; id: number } | null,
    /** Loading state for initial load */
    timelineIndividualLoading: false,
    /** Loading state for load more operation */
    timelineIndividualLoadingMore: false,

    /**
     * Tracks the (sortKey, sortOrder) each project-folder cache entry was
     * fetched with — and when — keyed by `${projectId}|${folderId || "root"}`.
     * Used by the project loaders to detect that a cached slot is stale because
     * the user toggled a column header, or because `fetchedAt` has aged past
     * PROJECT_DOC_CACHE_TTL_MS. In both cases we refetch *without* clearing the
     * cache, so the table shows the rows it already had underneath the loading
     * overlay until the fresh ones arrive. Without this, sort changes on
     * /projects briefly empty the list and the page scroll position jumps as
     * the document list collapses to height 0.
     */
    projectFolderSortMeta: new Map<
      string,
      { sortKey?: SortKey; sortOrder?: SortOrder; fetchedAt: number }
    >(),

    /**
     * The sort each slot was last *asked* for, keyed like
     * projectFolderSortMeta and claimed by the loaders before they leave.
     *
     * projectFolderSortMeta only learns a sort once a fetch commits, so during
     * a sort change it still names the previous one. A peek reading it there
     * would fetch the OLD order, claim the slot after the loader (they share
     * projectFolderRequestSeq) and write those rows back stamped fresh —
     * leaving the folder view rendering an order its own sort control
     * disagrees with, and nothing in flight to correct it. Peeks read this
     * instead, so they fetch the sort the view is switching to. An invalidation
     * never clears it — it describes the view's intent, not cached rows. A
     * FAILED fetch does (releasePendingSortClaim): nothing will commit that
     * sort, so the claim would otherwise send the next peek after an order no
     * view is showing, and the peek would stamp the slot with it.
     */
    projectFolderPendingSort: new Map<string, { sortKey?: SortKey; sortOrder?: SortOrder }>(),

    /**
     * Monotonic write counter per project-folder slot, keyed exactly like
     * projectFolderSortMeta. Only the newest fetch for a slot may write it —
     * and loaders and peeks claim the SAME counter, because they write the
     * same slot: two sequences would order each writer only against its own
     * kind, and an older loader landing after a newer peek would still put the
     * superseded rows back.
     *
     * Both are called concurrently by design — a resync fires while a user
     * navigation is in flight, a picker peeks the folder open behind it — and
     * responses can land out of order. Without this, an older response landing
     * last would put its rows back AND stamp them `fetchedAt: Date.now()`, so
     * the cache would then serve those stale rows as fresh for the whole TTL:
     * exactly the superseded-anchor state the freshness window exists to end.
     */
    projectFolderRequestSeq: new Map<string, number>(),

    /**
     * Which loader owns `isLoadingProjectDocs`. Tracked apart from the write
     * claim above precisely because that one is shared with peeks: a peek must
     * be able to stop an older loader writing the slot without also stranding
     * the folder view's spinner on a loader whose `finally` then declines to
     * clear it. Loaders only, newest wins.
     */
    projectDocsLoadSeq: 0,

    /**
     * Monotonic counter bumped by every all-projects `invalidateProjectCache`.
     * Unlike projectFolderRequestSeq it is not per slot: an invalidation says
     * nothing about which fetches are already on the wire, so any response
     * that left before it is suspect and is fetched again rather than written
     * back. See fetchOutlastingInvalidations.
     */
    projectCacheGeneration: 0,

    /**
     * The same counter per project, bumped by `invalidateProjectCache(id)`.
     * Almost every invalidation names a project, and a fetch is only suspect
     * when the invalidation concerned its own rows — otherwise opening the
     * picker on one project would send the documents page's in-flight loads
     * for another back to the network for identical rows, once per folder.
     * Read through projectCacheGenerationFor, never on its own.
     */
    projectCacheGenerationByProject: new Map<string, number>(),
  }))
  .views((self) => ({
    /**
     * Get all documents as an array in DB-returned order.
     *
     * `loadDocuments` and `goToPage` insert filename matches first (in DB sort
     * order via the repository's ORDER BY) and then append content-only search
     * matches, so the Map's natural iteration order already gives the right
     * grouping. Returning insertion order here lets the DB ORDER BY clause be
     * the single source of truth for sort, so paginated sorts stay contiguous
     * across page boundaries. (WHA-3277)
     */
    get documentsArray() {
      // Collapse each version lineage to its latest row so the list never shows
      // an older version (and its stale tags) — superseded rows can linger in
      // the map between the new-version INSERT and the old-row is_latest flip
      // realtime events.
      return keepLatestVersions(Array.from(self.documents.values()));
    },

    /**
     * Get count of selected documents.
     */
    get selectedCount() {
      return Array.from(self.selection.selectedIds.values()).filter(Boolean).length;
    },

    /**
     * Check if all documents are selected.
     */
    get allSelected() {
      const totalDocs = self.documents.size;
      if (totalDocs === 0) return false;

      const selectedCount = Array.from(self.selection.selectedIds.values()).filter(Boolean).length;
      return selectedCount === totalDocs;
    },

    /**
     * Check if a document is selected.
     */
    isSelected(docId: string) {
      return self.selection.selectedIds.get(docId) ?? false;
    },

    /**
     * Get array of selected document IDs.
     */
    get selectedDocumentIds(): string[] {
      return selectedIdsInClickOrder(self.selection.selectedIds);
    },

    /**
     * Every document row the user can currently select, unioned across ALL
     * surfaces that render selectable rows — the MST `documents` map, the
     * project-scoped row cache, the timeline's individual-docs cache, and
     * expanded-cluster rows — plus any `extraRows` the caller renders that live
     * outside the store entirely (e.g. the `documents` prop passed to
     * `DocumentsMainView`). Deduped by id, first surface wins.
     *
     * This is the SINGLE place selection is resolved to full objects. Every
     * bulk action must go through this (or `selectedDocuments`): resolving a
     * selection against only `self.documents` is exactly the timeline
     * "Sign 0 docs" / "download skips timeline rows" bug.
     */
    allSelectableDocuments(extraRows: SelectableDocument[] = []): SelectableDocument[] {
      const _version = self.projectCacheVersion; // volatile cache — reactivity via the counter
      return dedupeSelectable([
        self.documents.values(),
        ...projectRowGroups(self.projectDocuments),
        self.timelineIndividualDocs,
        ...Array.from(self.expandedClusters.values(), (cluster) => cluster.documents),
        extraRows,
      ]);
    },

    /**
     * The current selection resolved to full document rows, in click order
     * (`selectedDocumentIds`), unioned across every surface (same sources as
     * `allSelectableDocuments`). Pass a surface's rendered rows as `extraRows`
     * when they live outside the store (project view).
     */
    selectedDocuments(extraRows: SelectableDocument[] = []): SelectableDocument[] {
      const byId = new Map<string, SelectableDocument>(
        this.allSelectableDocuments(extraRows).map((doc) => [String(doc.id), doc])
      );
      return selectedIdsInClickOrder(self.selection.selectedIds)
        .map((id) => byId.get(id))
        .filter((doc): doc is SelectableDocument => doc !== undefined);
    },

    /**
     * The selected rows, reduced to what a Verified decision needs.
     *
     * Selection ids alone aren't enough — the action has to know each row's
     * project (to gate on MODIFY_PROJECT_FILES), its current mark (to label
     * Verify vs Remove verification) and its filename (chat attachments). Rows
     * are looked up in the MST map first and then in the timeline's own row
     * lists, because the Timeline tab never populates `documents`. Ids that
     * resolve nowhere are dropped rather than guessed at.
     */
    get selectedVerifiableDocuments(): Array<{
      id: string;
      filename: string;
      project_id: number | null;
      verified_at: string | null;
    }> {
      const ids = Array.from(self.selection.selectedIds.entries())
        .filter(([, isSelected]) => isSelected)
        .map(([id]) => id);
      if (ids.length === 0) return [];

      const timelineRows = new Map<string, DocumentRowMapped>();
      const collect = (rows: readonly DocumentRowMapped[]) => {
        for (const row of rows) timelineRows.set(String(row.id), row);
      };
      collect(self.timelineIndividualDocs);
      collect(self.timelineIndividualServerDocs);
      collect(self.timelineContentMatchDocs);
      for (const cluster of self.expandedClusters.values()) collect(cluster.documents);

      return ids
        .map((id) => {
          const model = self.documents.get(id);
          if (model) {
            return {
              id,
              filename: model.filename,
              project_id: model.project_id ?? null,
              verified_at: model.verified_at ?? null,
            };
          }
          const row = timelineRows.get(id);
          if (row) {
            return {
              id,
              filename: row.filename,
              project_id: row.project_id ?? null,
              verified_at: row.verified_at ?? null,
            };
          }
          return null;
        })
        .filter((doc): doc is NonNullable<typeof doc> => doc !== null);
    },

    /**
     * Resolve a full timeline row by document id, across every timeline row
     * source (individual docs, server docs, content matches, expanded
     * clusters). The Timeline tab's context menu uses this to retarget
     * single-row actions at the checked row when a different row was
     * right-clicked — same rule as DocumentListTable's buildDocMenuConfig.
     */
    /**
     * Every document id the Timeline tab can currently render, across its
     * row sources — what the comment-count badges are keyed on.
     */
    get timelineVisibleDocumentIds(): number[] {
      const ids = new Set<number>();
      const collect = (rows: readonly DocumentRowMapped[]) => {
        for (const row of rows) ids.add(Number(row.id));
      };
      collect(self.timelineIndividualDocs);
      collect(self.timelineIndividualServerDocs);
      collect(self.timelineContentMatchDocs);
      for (const cluster of self.expandedClusters.values()) collect(cluster.documents);
      return Array.from(ids);
    },

    timelineRowById(docId: string): DocumentRowMapped | undefined {
      const target = String(docId);
      const find = (rows: readonly DocumentRowMapped[]) =>
        rows.find((row) => String(row.id) === target);
      const direct =
        find(self.timelineIndividualDocs) ??
        find(self.timelineIndividualServerDocs) ??
        find(self.timelineContentMatchDocs);
      if (direct) return direct;
      for (const cluster of self.expandedClusters.values()) {
        const hit = find(cluster.documents);
        if (hit) return hit;
      }
      return undefined;
    },

    /**
     * Check if there are more documents to load.
     * Simply compares loaded count vs total count.
     */
    get hasMore() {
      return self.documents.size < self.totalCount;
    },

    /**
     * Total number of pages based on totalCount and pageSize.
     */
    get totalPages() {
      // Content-only matches ride on page 1 and are not paged, so they must
      // not add a page of their own.
      return Math.max(
        self.totalCount > 0 ? 1 : 0,
        Math.ceil((self.totalCount - self.contentOnlyMatchCount) / self.pageSize)
      );
    },

    /**
     * Get documents for the current project and folder from cache.
     * Returns empty array if no project is selected or cache is empty.
     * Depends on projectCacheVersion to trigger reactivity when volatile cache updates.
     */
    get currentProjectDocuments(): any[] {
      // Access projectCacheVersion to establish MobX dependency for reactivity
      const _version = self.projectCacheVersion;
      if (!self.currentProjectId) return [];
      const projectCache = self.projectDocuments.get(self.currentProjectId);
      if (!projectCache) return [];
      const key = self.currentFolderId || "root";
      // Collapse version lineages to their latest row (see documentsArray).
      return keepLatestVersions(projectCache.get(key) || []);
    },

    /**
     * Look up cached documents for a specific project + folder.
     * Unlike currentProjectDocuments, this does NOT depend on
     * currentFolderId/currentProjectId, so it cannot be affected
     * by other actions mutating global navigation state.
     *
     * Cache slot is per-folder; sort variants do not get separate slots —
     * callers invoke `invalidateProjectCache` and reload when sort changes,
     * mirroring the `/documents` requery-on-toggle behaviour.
     */
    getProjectDocuments(projectId: string, folderId: string | null): any[] {
      const _version = self.projectCacheVersion; // establish MobX dependency
      const projectCache = self.projectDocuments.get(projectId);
      if (!projectCache) return [];
      const key = folderId || "root";
      // Collapse version lineages to their latest row (see documentsArray).
      return keepLatestVersions(projectCache.get(key) || []);
    },

    // ========================================================================
    // Timeline Views (Server-Side Aggregation)
    // ========================================================================

    /**
     * Get timeline buckets from the summary.
     * Each bucket contains count and time range, NOT individual documents.
     */
    get timelineBuckets() {
      return self.timelineSummary?.buckets ?? [];
    },

    /**
     * Get the full time range of the timeline.
     */
    get timelineTimeRange(): { start: Date; end: Date } | null {
      return self.timelineSummary?.timeRange ?? null;
    },

    /**
     * Get total document count from timeline summary.
     */
    get timelineTotalDocuments(): number {
      return self.timelineSummary?.totalDocuments ?? 0;
    },

    /**
     * Get expanded cluster state by time range.
     * Returns the MST model directly for proper MobX reactivity.
     * Returns undefined if the cluster is not expanded.
     */
    getExpandedCluster(startTime: Date, endTime: Date) {
      const key = `${startTime.toISOString()}|${endTime.toISOString()}`;
      // Return MST model directly - DO NOT convert to plain object!
      // Converting breaks MobX reactivity since the plain object won't update
      // when the MST model changes.
      return self.expandedClusters.get(key);
    },

    /**
     * Check if a document was found via content search (not filename match).
     */
    isContentMatch(docId: string): boolean {
      return self.contentMatchDocIds.has(docId);
    },

    /**
     * Get content-match metadata (first matching page/sheet + count) for a doc.
     * Returns null when no metadata is recorded — typically because the doc
     * came from a non-search load.
     */
    getContentMatchInfo(docId: string): ContentMatchInfo | null {
      return self.contentMatchInfoById.get(docId) ?? null;
    },

    /**
     * Check if a document was found via content search in the timeline view.
     */
    isTimelineContentMatch(docId: string): boolean {
      return self.timelineContentMatchIds.has(docId);
    },

    /** Per-doc match metadata for the timeline Content chip tooltip. */
    getTimelineContentMatchInfo(docId: string): ContentMatchInfo | null {
      return self.timelineContentMatchInfoById.get(docId) ?? null;
    },

    /**
     * Check if a cluster is currently expanded.
     */
    isClusterExpanded(startTime: Date, endTime: Date): boolean {
      const key = `${startTime.toISOString()}|${endTime.toISOString()}`;
      return self.expandedClusters.has(key);
    },
  }))
  .actions((self) => ({
    /**
     * Clear all documents and reset state.
     */
    clear() {
      self.documents.clear();
      self.selection.selectedIds.clear();
      self.isLoading = false;
      self.error = null;
      self.currentOffset = 0;
      self.totalCount = 0;
      self.contentOnlyMatchCount = 0;
      self.isLoadingMore = false;
      self.currentFilters = {};
      // Reset project document state
      self.currentProjectId = null;
      self.currentFolderId = null;
      self.isLoadingProjectDocs = false;
      self.projectDocuments.clear();
      // Reset processing count and tracking
      self.processingCount = 0;
      self.processingDocIds.clear();
      // Reset content search state
      self.contentMatchDocIds = new Set();
      self.contentMatchInfoById = new Map();
    },

    /**
     * Set the processing document count and ID set (for deduplication).
     */
    setProcessingCountAndIds(count: number, ids: Set<string>) {
      self.processingCount = count;
      self.processingDocIds = ids;
    },

    /**
     * Track a processing document (handles duplicates).
     * Only increments if not already tracked.
     */
    trackProcessingDoc(docId: string) {
      if (!self.processingDocIds.has(docId)) {
        self.processingDocIds.add(docId);
        self.processingCount += 1;
      }
    },

    /**
     * Untrack a processing document (handles duplicates).
     * Only decrements if was tracked.
     */
    untrackProcessingDoc(docId: string) {
      if (self.processingDocIds.has(docId)) {
        self.processingDocIds.delete(docId);
        if (self.processingCount > 0) {
          self.processingCount -= 1;
        }
      }
    },

    /**
     * Refresh the processing document count and IDs from the database.
     * Syncs the tracking set for accurate WebSocket deduplication.
     */
    refreshProcessingCount: flow(function* refreshProcessingCount(
      buildingId: string,
      projectId?: string | null
    ) {
      try {
        const result: { ids: Set<string>; count: number } =
          yield documentsRepo.fetchProcessingDocumentIdsAndCount(buildingId, projectId);
        self.processingCount = result.count;
        self.processingDocIds = result.ids;
      } catch (e) {
        console.error("Failed to refresh processing count", e);
      }
    }),

    /**
     * Reset pagination state and clear documents.
     */
    resetPagination() {
      self.currentOffset = 0;
      self.currentPage = 1;
      self.totalCount = 0;
      self.contentOnlyMatchCount = 0;
      self.documents.clear();
      self.selection.selectedIds.clear();
      self.currentFilters = {};
      self.contentMatchDocIds = new Set();
      self.contentMatchInfoById = new Map();
    },

    /**
     * Set content match document IDs externally (e.g., from project document search).
     */
    setContentMatchDocIds(ids: Set<string>) {
      self.contentMatchDocIds = ids;
    },

    /**
     * Set per-document content-match metadata externally. Mirrors
     * setContentMatchDocIds and is called by /projects so the Content chip
     * tooltip can read from the same store instance the rows render from.
     */
    setContentMatchInfoById(info: Map<string, ContentMatchInfo>) {
      self.contentMatchInfoById = info;
    },

    /**
     * Set the page size for subsequent loads.
     * Used to configure different page sizes (e.g., 5 for initial load, 50 for load more).
     */
    setPageSize(size: number) {
      self.pageSize = size;
    },

    /**
     * Set state filter for document queries.
     * @param states - Array of states to include (e.g., ['COMPLETE', 'ERROR'])
     * @param excludeStates - Array of states to exclude (e.g., ['COMPLETE', 'ERROR'])
     */
    setStateFilter(states: string[] | null, excludeStates: string[] | null = null) {
      self.stateFilter = states;
      self.excludeStateFilter = excludeStates;
    },

    /**
     * Load documents for a building.
     * Resets pagination state and fetches fresh data.
     * Stores filters for pagination continuity in loadMoreDocuments().
     *
     * @param buildingId - The building ID
     * @param filters - Optional filter parameters (states, categories, search, etc.)
     */
    loadDocuments: flow(function* loadDocuments(buildingId: string, filters?: DocumentFilters) {
      const root: any = getRoot(self);
      const auth = root?.authStore;
      if (!auth?.isAuthenticated) {
        self.error = "Authentication required";
        throw new Error("Authentication required");
      }

      // Claim the newest generation. Everything between here and the first
      // yield is request-START state, where last-started-wins is what we want;
      // the guard below covers the writes that land after the awaits.
      const seq = ++self.documentsRequestSeq;

      // Store filters for pagination continuity
      self.currentFilters = filters ?? {};

      // Also update volatile state filters for backward compatibility
      self.stateFilter = filters?.states ?? null;
      self.excludeStateFilter = filters?.excludeStates ?? null;

      // When the building changes, immediately drop the previous building's
      // documents (and selection) so they don't flash into — or get acted on
      // from — the new building's /documents view. Same-building filter changes
      // still keep old docs visible during refetch.
      applyBuildingScope(self, buildingId);

      // Don't clear documents yet - keep showing existing ones while loading
      // This prevents jarring full-screen flash when changing filters
      self.isLoading = true;
      self.error = null;

      try {
        self.contentOnlyMatchCount = 0;

        // Run filename search and content search in parallel. The content
        // search is held to the same filters, and is a no-op without text.
        const [filenameResult, contentResult] = yield Promise.all([
          documentsRepo.listDocuments({
            buildingId,
            limit: self.pageSize,
            offset: 0,
            ...self.currentFilters,
          }),
          searchFilteredDocumentContent({ buildingId, ...self.currentFilters }),
        ]);

        const { data, totalCount } = filenameResult;
        const contentMatchInfo: Map<number, ContentMatchInfo> = contentResult.matchInfo;

        // Track which document IDs came from filename search
        const filenameDocIds = new Set(data.map((doc: DocumentRowMapped) => doc.id));

        // Find content-matched IDs not already in filename results. The RPC
        // already excludes filename matches (WHA-3179) so the filter is a
        // safety net for races where listDocuments and searchDocumentContent
        // disagree.
        const contentOnlyIds = Array.from(
          (contentMatchInfo as Map<number, ContentMatchInfo>).keys()
        ).filter((id: number) => !filenameDocIds.has(id));

        const contentOnlyIdSet = new Set(contentOnlyIds);
        const contentOnlyDocs: DocumentRowMapped[] = contentResult.documents.filter(
          (doc: DocumentRowMapped) => contentOnlyIdSet.has(doc.id)
        );

        // A newer load (or a page change) started while this one was in flight.
        // Bail before the first write rather than at each one: the block below
        // clears `documents` and rebuilds it, so a superseded response must not
        // reach even the clear — otherwise it blanks the newer term's rows on
        // its way to painting the older term's.
        if (seq !== self.documentsRequestSeq) return;

        // Build the content match ID set (content-only docs, not filename matches)
        const newContentMatchDocIds = new Set<string>(
          contentOnlyDocs.map((doc: DocumentRowMapped) => String(doc.id))
        );

        // Project the per-doc page-info map onto string IDs (matching the chip
        // store's keying) — used by the Content chip tooltip.
        const newContentMatchInfoById = new Map<string, ContentMatchInfo>();
        for (const doc of contentOnlyDocs) {
          const info = (contentMatchInfo as Map<number, ContentMatchInfo>).get(doc.id);
          if (info) newContentMatchInfoById.set(String(doc.id), info);
        }

        // Now clear and replace atomically - old docs stay visible until this point
        self.documents.clear();
        self.searchStatus = contentResult.status;
        self.contentMatchDocIds = newContentMatchDocIds;
        self.contentMatchInfoById = newContentMatchInfoById;

        // Update pagination state (filename total + content-only extras)
        self.totalCount = totalCount + contentOnlyDocs.length;
        self.contentOnlyMatchCount = contentOnlyDocs.length;
        self.currentOffset = 0;
        self.currentPage = 1;

        // Helper to add a doc to the store
        const addDoc = (doc: DocumentRowMapped) => {
          self.documents.set(
            String(doc.id),
            DocumentModel.create({
              id: String(doc.id),
              filename: doc.filename,
              file_path: doc.file_path,
              building_id: String(doc.building_id),
              parent_document_id: doc.parent_document_id ? String(doc.parent_document_id) : null,
              content_type: doc.content_type,
              size: doc.size,
              created_at: doc.created_at,
              original_created_at: doc.original_created_at ?? null,
              modified_at: doc.modified_at ?? null,
              document_content_modified_date: doc.document_content_modified_date ?? null,
              effective_modified_at: doc.effective_modified_at ?? null,
              created_by: doc.created_by,
              created_by_name: doc.created_by_name ?? null,
              created_by_avatar_url: doc.created_by_avatar_url ?? null,
              created_by_color: doc.created_by_color ?? null,
              state: doc.state ?? null,
              version: doc.version ?? null,
              version_group: doc.version_group ?? null,
              source: doc.source ?? null,
              container_type: doc.container_type ?? null,
              project_id: doc.project_id ?? null,
              folder_id: doc.folder_id ? String(doc.folder_id) : null,
              is_deleted: doc.is_deleted ?? false,
              approved: doc.approved ?? false,
              verified_at: doc.verified_at ?? null,
              verified_by: doc.verified_by ?? null,
              verified_by_name: doc.verified_by_name ?? null,
              categories: doc.categories ?? [],
              design_disciplines: doc.design_disciplines ?? [],
              design_stages: doc.design_stages ?? [],
              linked_issues: doc.linked_issues ?? [],
            })
          );
        };

        // Add filename matches first, then content-only matches
        data.forEach(addDoc);
        contentOnlyDocs.forEach(addDoc);
        // Stamp for revalidateVisibleDocuments' freshness check.
        self.documentsFetchedAt = Date.now();
      } catch (e: any) {
        console.error("loadDocuments error", e);
        // A superseded request's failure must not paint an error over the rows
        // a newer one is about to deliver. Still rethrown either way — the
        // caller has its own cancellation flag and decides what to do.
        if (seq === self.documentsRequestSeq) {
          self.error = e instanceof Error ? e.message : String(e);
        }
        throw e;
      } finally {
        // Only the newest request may end the loading state. Otherwise the
        // first of two overlapping searches to settle turns the spinner off
        // while the one whose rows will actually be shown is still running.
        if (seq === self.documentsRequestSeq) self.isLoading = false;
      }
    }),

    /**
     * Load more documents for the current building (append to existing).
     * Used for infinite scroll / lazy loading.
     * Preserves filter state from loadDocuments() for pagination continuity.
     */
    loadMoreDocuments: flow(function* loadMoreDocuments(buildingId: string) {
      // Guard: don't load if already loading or no more data
      if (self.isLoadingMore || !self.hasMore) {
        return;
      }

      const root: any = getRoot(self);
      const auth = root?.authStore;
      if (!auth?.isAuthenticated) {
        self.error = "Authentication required";
        throw new Error("Authentication required");
      }

      // Observed, never bumped: an append must not invalidate a replacement
      // that starts while it is in flight, but it must drop itself if one does
      // — its rows belong to the offset and filters of a list that no longer
      // exists.
      const seq = self.documentsRequestSeq;

      self.isLoadingMore = true;
      self.error = null;

      try {
        // The filename rows already loaded. Content-only matches live outside
        // filename pagination: counting them skipped that many filename rows.
        const newOffset = self.documents.size - self.contentMatchDocIds.size;

        // Guard: every filename row is already here.
        if (self.totalCount > 0 && newOffset >= self.totalCount - self.contentOnlyMatchCount) {
          return;
        }

        // Use stored filters for pagination consistency
        const { data, totalCount } = yield documentsRepo.listDocuments({
          buildingId,
          limit: self.pageSize,
          offset: newOffset,
          ...self.currentFilters,
        });

        // A filter change or page change replaced the list under us.
        if (seq !== self.documentsRequestSeq) return;

        // Update pagination state — the content-only matches still count.
        self.totalCount = totalCount + self.contentOnlyMatchCount;
        self.currentOffset = newOffset;

        // Append documents to map (not replace)
        data.forEach((doc) => {
          self.documents.set(
            String(doc.id),
            DocumentModel.create({
              id: String(doc.id),
              filename: doc.filename,
              file_path: doc.file_path,
              building_id: String(doc.building_id),
              parent_document_id: doc.parent_document_id ? String(doc.parent_document_id) : null,
              content_type: doc.content_type,
              size: doc.size,
              created_at: doc.created_at,
              original_created_at: doc.original_created_at ?? null,
              modified_at: doc.modified_at ?? null,
              document_content_modified_date: doc.document_content_modified_date ?? null,
              effective_modified_at: doc.effective_modified_at ?? null,
              created_by: doc.created_by,
              created_by_name: doc.created_by_name ?? null,
              created_by_avatar_url: doc.created_by_avatar_url ?? null,
              created_by_color: doc.created_by_color ?? null,
              state: doc.state ?? null,
              version: doc.version ?? null,
              version_group: doc.version_group ?? null,
              source: doc.source ?? null,
              container_type: doc.container_type ?? null,
              project_id: doc.project_id ?? null,
              folder_id: doc.folder_id ? String(doc.folder_id) : null,
              is_deleted: doc.is_deleted ?? false,
              approved: doc.approved ?? false,
              verified_at: doc.verified_at ?? null,
              verified_by: doc.verified_by ?? null,
              verified_by_name: doc.verified_by_name ?? null,
              // Tag-related fields from junction tables
              categories: doc.categories ?? [],
              design_disciplines: doc.design_disciplines ?? [],
              design_stages: doc.design_stages ?? [],
              linked_issues: doc.linked_issues ?? [],
            })
          );
        });
      } catch (e: any) {
        console.error("loadMoreDocuments error", e);
        if (seq === self.documentsRequestSeq) {
          self.error = e instanceof Error ? e.message : String(e);
          // On error (especially 416 Range Not Satisfiable), assume no more data
          // This prevents infinite retry loops
          self.totalCount = self.documents.size;
        }
      } finally {
        self.isLoadingMore = false;
      }
    }),

    /**
     * Navigate to a specific page of documents.
     * Clears current documents and fetches the page from the server.
     */
    goToPage: flow(function* goToPage(buildingId: string, page: number) {
      const root: any = getRoot(self);
      const auth = root?.authStore;
      if (!auth?.isAuthenticated) {
        self.error = "Authentication required";
        throw new Error("Authentication required");
      }

      // Shares loadDocuments' counter: both replace `documents` wholesale, so
      // they must be able to supersede each other.
      const seq = ++self.documentsRequestSeq;

      self.currentPage = page;
      self.isLoading = true;
      self.error = null;

      try {
        const offset = (page - 1) * self.pageSize;
        const hasSearchText = !!self.currentFilters.searchText?.trim();

        const [filenameResult, contentResult] = yield Promise.all([
          documentsRepo.listDocuments({
            buildingId,
            limit: self.pageSize,
            offset,
            ...self.currentFilters,
          }),
          // Only run content search on page 1 (content-only matches are appended once)
          page === 1
            ? searchFilteredDocumentContent({ buildingId, ...self.currentFilters })
            : Promise.resolve(null),
        ]);

        const { data, totalCount } = filenameResult;
        const contentMatchInfo: Map<number, ContentMatchInfo> =
          contentResult?.matchInfo ?? new Map();

        // Track content-only matches (only on page 1)
        const filenameDocIds = new Set(data.map((doc: DocumentRowMapped) => doc.id));
        const contentOnlyIds = Array.from(
          (contentMatchInfo as Map<number, ContentMatchInfo>).keys()
        ).filter((id: number) => !filenameDocIds.has(id));
        const contentOnlyIdSet = new Set(contentOnlyIds);
        const contentOnlyDocs: DocumentRowMapped[] = (contentResult?.documents ?? []).filter(
          (doc: DocumentRowMapped) => contentOnlyIdSet.has(doc.id)
        );
        // Superseded — bail before the clear, as in loadDocuments.
        if (seq !== self.documentsRequestSeq) return;

        const contentOnlyMatchCount =
          hasSearchText && page === 1 ? contentOnlyDocs.length : self.contentOnlyMatchCount;

        const newContentMatchDocIds = new Set<string>(
          contentOnlyDocs.map((doc: DocumentRowMapped) => String(doc.id))
        );
        const newContentMatchInfoById = new Map<string, ContentMatchInfo>();
        for (const doc of contentOnlyDocs) {
          const info = (contentMatchInfo as Map<number, ContentMatchInfo>).get(doc.id);
          if (info) newContentMatchInfoById.set(String(doc.id), info);
        }

        self.documents.clear();
        if (contentResult) self.searchStatus = contentResult.status;
        self.contentMatchDocIds = newContentMatchDocIds;
        self.contentMatchInfoById = newContentMatchInfoById;
        self.totalCount = totalCount + contentOnlyMatchCount;
        self.contentOnlyMatchCount = contentOnlyMatchCount;
        self.currentOffset = offset;

        const addDoc = (doc: DocumentRowMapped) => {
          self.documents.set(
            String(doc.id),
            DocumentModel.create({
              id: String(doc.id),
              filename: doc.filename,
              file_path: doc.file_path,
              building_id: String(doc.building_id),
              parent_document_id: doc.parent_document_id ? String(doc.parent_document_id) : null,
              content_type: doc.content_type,
              size: doc.size,
              created_at: doc.created_at,
              original_created_at: doc.original_created_at ?? null,
              modified_at: doc.modified_at ?? null,
              document_content_modified_date: doc.document_content_modified_date ?? null,
              effective_modified_at: doc.effective_modified_at ?? null,
              created_by: doc.created_by,
              created_by_name: doc.created_by_name ?? null,
              created_by_avatar_url: doc.created_by_avatar_url ?? null,
              created_by_color: doc.created_by_color ?? null,
              state: doc.state ?? null,
              version: doc.version ?? null,
              version_group: doc.version_group ?? null,
              source: doc.source ?? null,
              container_type: doc.container_type ?? null,
              project_id: doc.project_id ?? null,
              folder_id: doc.folder_id ? String(doc.folder_id) : null,
              is_deleted: doc.is_deleted ?? false,
              approved: doc.approved ?? false,
              verified_at: doc.verified_at ?? null,
              verified_by: doc.verified_by ?? null,
              verified_by_name: doc.verified_by_name ?? null,
              categories: doc.categories ?? [],
              design_disciplines: doc.design_disciplines ?? [],
              design_stages: doc.design_stages ?? [],
              linked_issues: doc.linked_issues ?? [],
            })
          );
        };

        data.forEach(addDoc);
        contentOnlyDocs.forEach(addDoc);
        // Stamp for revalidateVisibleDocuments' freshness check.
        self.documentsFetchedAt = Date.now();
      } catch (e: any) {
        console.error("goToPage error", e);
        if (seq === self.documentsRequestSeq) {
          self.error = e instanceof Error ? e.message : String(e);
        }
        throw e;
      } finally {
        if (seq === self.documentsRequestSeq) self.isLoading = false;
      }
    }),

    /**
     * Select or deselect a document.
     * Deselect deletes the key (never `.set(id, false)`): `Map.set` on an
     * existing key keeps its old position, so re-selecting would replay the
     * stale insertion order — selectedDocumentIds must reflect click order
     * (compare base/changed depends on it).
     */
    selectDocument(docId: string, isSelected: boolean) {
      if (isSelected) {
        self.selection.selectedIds.set(docId, true);
      } else {
        self.selection.selectedIds.delete(docId);
      }
    },

    /**
     * Select or deselect all documents.
     */
    selectAll(isSelected: boolean) {
      Array.from(self.documents.keys()).forEach((docId) => {
        if (isSelected) {
          self.selection.selectedIds.set(docId, true);
        } else {
          self.selection.selectedIds.delete(docId);
        }
      });
    },

    /**
     * Clear all selections.
     */
    clearSelection() {
      self.selection.selectedIds.clear();
    },

    /**
     * Tell the store which building — and which project inside it — the user is
     * looking at, dropping what belonged to the scope they left (see
     * `applyBuildingScope` / `applyProjectScope`). `projectId` is null at
     * whole-building scope.
     *
     * Two callers: the documents page, which owns the global scope every tab
     * renders under, and `ProjectScopedFolderView`, which scopes itself so the
     * standalone /projects/:id route (no documents page above it) is covered
     * too. `loadDocuments` applies the building half as well, but the folder
     * surface never calls `loadDocuments` — that is where a selection could
     * survive a scope change and be recovered, and acted on, from the next one.
     */
    setDocumentScope(buildingId: string, projectId: string | null) {
      applyBuildingScope(self, buildingId);
      applyProjectScope(self, projectId);
    },

    /**
     * The selection resolved to full rows for an action that runs OUTSIDE a
     * store flow — the Sign dialog, which needs the same server recovery bulk
     * download and bulk trash do (see `resolveSelectionForAction`). Rejects if
     * the recovery query fails, so the caller can refuse to act on a selection
     * it could not verify.
     */
    resolveSelectionForBulkAction: flow(function* resolveSelectionForBulkAction(
      extraRows: SelectableDocument[] = [],
      folderIds: string[] = []
    ) {
      const resolved: ResolvedSelection = yield resolveSelectionForAction(
        self,
        extraRows,
        folderIds
      );
      return resolved;
    }),

    /**
     * Delete a document (soft delete).
     * Shows toast notifications.
     *
     * ⚠️ IMPORTANT: Confirmation is handled at the UI layer using ConfirmDialog.
     * This action is destructive and must be confirmed by the user before calling.
     *
     * @param docId - Document ID to delete
     *
     * @example
     * // UI layer handles confirmation
     * const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
     *
     * // Trigger
     * <Button onClick={() => setShowDeleteConfirm(true)}>Delete</Button>
     *
     * // Dialog
     * <ConfirmDialog
     *   open={showDeleteConfirm}
     *   onOpenChange={setShowDeleteConfirm}
     *   title="Delete Document?"
     *   description={`Are you sure you want to delete "${filename}"?`}
     *   onConfirm={() => {
     *     store.deleteDocument(docId);
     *     setShowDeleteConfirm(false);
     *   }}
     *   confirmLabel="Delete"
     *   variant="destructive"
     * />
     */
    deleteDocument: flow(function* deleteDocument(docId: string) {
      const root: any = getRoot(self);
      const userStore = root?.userStore;
      const userId = userStore?.userProfile?.id;

      if (!userId) {
        toast({
          title: "Error",
          description: "User authentication required",
          variant: "destructive",
        });
        throw new Error("User profile missing");
      }

      // Capture filename + project/building context BEFORE the delete so we can:
      //   (a) show the "\"<filename>\" has been deleted" toast (WHA-3280) — docs
      //       on /documents live in self.documents, docs on /projects live only
      //       in self.projectDocuments, so we fall back to the project cache when
      //       the row isn't in the main MST map
      //   (b) refresh the folder document-count map so a folder that just had its
      //       last doc removed doesn't keep showing the stale count (WHA-3258)
      const mstDoc = self.documents.get(docId);
      let filename: string | null = mstDoc?.filename ?? null;
      let docProjectId: number | null = mstDoc?.project_id ?? null;
      let docBuildingId: string | null = mstDoc?.building_id ?? null;
      if (!mstDoc) {
        outer: for (const projectCache of self.projectDocuments.values()) {
          for (const docs of projectCache.values()) {
            const found = (docs as any[]).find((d) => String(d.id) === docId);
            if (found) {
              filename = found.filename ?? null;
              docProjectId = found.project_id ?? null;
              docBuildingId = found.building_id != null ? String(found.building_id) : null;
              break outer;
            }
          }
        }
      }

      try {
        // An issue attachment says why in its own toast and stays.
        const [trashableId]: string[] = yield withoutPinnedDocuments([docId], "trash");
        if (!trashableId) return;
        yield documentsRepo.deleteDocument(docId, String(userId));

        // Remove from main MST map (no-op if the row was only in the project
        // cache, e.g. when the action was triggered from /projects) plus the
        // children the DB trigger cascade-deleted, then from the timeline's own
        // row copies — same fan-out the bulk trash patches, or a delete made on
        // the timeline leaves its card (and its children's) rendered.
        const removedIds = [docId, ...collectCascadeDescendants(cascadeRowGroups(self), [docId])];
        removedIds.forEach((id) => {
          self.documents.delete(id);
          self.selection.selectedIds.delete(id);
        });
        removeTrashedRowsFromTimeline(self, removedIds);

        // Targeted removal from the projectDocuments cache so /projects
        // re-renders with the doc gone, without a full refetch. (WHA-3280)
        let patched = false;
        for (const projectCache of self.projectDocuments.values()) {
          for (const [folderKey, docs] of projectCache.entries()) {
            const arr = docs as any[];
            const next = arr.filter((d) => String(d.id) !== docId);
            if (next.length !== arr.length) {
              projectCache.set(folderKey, next);
              patched = true;
            }
          }
        }
        if (patched) self.projectCacheVersion += 1;

        // Refresh per-folder document counts so folder rows reflect the delete
        // immediately. Server-side aggregate so it stays accurate even when the
        // doc was nested several levels deep in the folder tree.
        if (docProjectId != null && docBuildingId) {
          fetchFolderDocumentCountsIntoStore(
            self,
            String(docProjectId),
            String(docBuildingId)
          ).catch((err: unknown) => {
            console.warn("Failed to refresh folder counts after delete", err);
          });
        }

        // Show success toast
        toast({
          title: "Success",
          description: filename ? `"${filename}" has been deleted` : "Document has been deleted",
        });
      } catch (e: any) {
        // A pinned file gets its own toast naming it, never the raw refusal.
        if (notifyIfPinned(e)) return;
        console.error("deleteDocument error", e);
        const errorMsg = e instanceof Error ? e.message : String(e);

        // Show error toast
        toast({
          title: "Error",
          description: `Failed to delete document: ${errorMsg}`,
          variant: "destructive",
        });

        throw new Error(errorMsg);
      }
    }),

    /**
     * Download a document.
     * Shows toast notifications for success/error.
     */
    downloadDocument: flow(function* downloadDocument(docId: string) {
      const t = getTranslationsStatic();
      const doc = self.documents.get(docId);
      let filename: string;

      if (doc) {
        filename = doc.filename;
      } else {
        // Document not in store (e.g., UNZIPPED container removed from store,
        // or child document inside a container never added to store).
        // Fall back to fetching from database.
        const dbDoc: any = yield documentsRepo.getDocumentById(docId);
        if (!dbDoc) {
          toast({
            title: t.common.error,
            description: "Document not found",
            variant: "destructive",
          });
          return;
        }
        filename = dbDoc.filename;
      }

      try {
        yield documentsRepo.downloadDocument(docId, filename);

        // Show success toast
        toast({
          title: t.common.success,
          description: t.documents.actions.downloadingFile.replace("{filename}", filename),
        });
      } catch (e: any) {
        console.error("downloadDocument error", e);
        const errorMsg = e instanceof Error ? e.message : String(e);

        // Show error toast
        toast({
          title: t.common.error,
          description: `Failed to download document: ${errorMsg}`,
          variant: "destructive",
        });

        throw new Error(errorMsg);
      }
    }),

    /**
     * Bulk download selected documents and/or folders.
     * Documents alone: single file downloads directly, multiple zip.
     * With folder IDs: zips everything with folder structure preserved.
     *
     * Resolves `true` when at least one file downloads or an explicitly selected
     * folder's structure is saved, even if individual files fail. Every abort path
     * (failed selection lookup, nothing to download, failed zip) and a files-only
     * selection where every requested file fails resolve
     * `false` rather than rejecting, so callers MUST gate post-action cleanup —
     * clearing the folder selection above all — on the result instead of on
     * `.then()` alone.
     */
    bulkDownload: flow(function* bulkDownload(
      documents?: Array<{ id: string; filename: string; file_path: string }>,
      folderIds?: string[],
      extraRows: SelectableDocument[] = [],
      downloadOptions: Pick<DownloadDocumentOptions, "surface"> = {}
    ) {
      const t = getTranslationsStatic();
      const folders = folderIds ?? [];

      // No explicit list: resolve the selection through the shared resolver so
      // timeline-selected rows (which live outside the MST `documents` map)
      // download too — the old `self.documents.get(id)` lookup returned null for
      // them and silently dropped every timeline doc from the zip. Callers whose
      // rows live outside the store entirely (e.g. a project-scoped `documents`
      // prop) must pass them as `extraRows`. Ids the resolver recovers are safe
      // to add alongside selected folders: a doc that also lives inside one of
      // them is zipped once (`bulkDownloadWithFolders` dedupes by id).
      //
      // An explicit `documents` list is downloaded as given — the caller has
      // already decided what the action covers.
      let docsToDownload: Array<{ id: string; filename: string; file_path: string }>;
      let unresolvedCount = 0;
      if (documents) {
        docsToDownload = documents.slice();
      } else {
        let resolved: ResolvedSelection;
        try {
          resolved = yield resolveSelectionForAction(self, extraRows, folders);
        } catch (e) {
          // A failed lookup is not "these documents are gone" — zipping the
          // rest would ship a silently short archive under a success toast.
          reportSelectionLookupFailure(e);
          return false;
        }
        docsToDownload = resolved.docs.map((doc) => ({
          id: String(doc.id),
          filename: doc.filename,
          file_path: doc.file_path,
        }));
        unresolvedCount = resolved.unresolvedCount;
      }

      // Selected ids nothing could resolve must not vanish silently — the
      // success toast would otherwise report fewer files than the selection
      // count promised.
      if (unresolvedCount > 0) {
        warnUnresolvedSelection(unresolvedCount);
        if (docsToDownload.length === 0 && folders.length === 0) return false;
      }

      if (docsToDownload.length === 0 && folders.length === 0) {
        toast({
          title: "No selection",
          description: "Please select documents or folders to download",
          variant: "destructive",
        });
        return false;
      }

      const totalUnits = docsToDownload.length + folders.length;

      try {
        toast({
          title: t.documents.actions.downloading,
          description:
            totalUnits === 1 && docsToDownload.length === 1
              ? t.documents.actions.downloadingFile.replace(
                  "{filename}",
                  docsToDownload[0].filename
                )
              : t.documents.actions.preparingDownload.replace("{count}", String(totalUnits)),
        });

        // A lone file downloads as itself; everything else goes through the
        // structure-preserving zip builder. Multi-document selections must not
        // bypass it even when no folder is checked — in search mode the rows are
        // subtree files, and zipping them flat is what dropped the subfolders.
        if (folders.length === 0 && docsToDownload.length === 1) {
          yield documentsRepo.bulkDownloadDocuments(docsToDownload, downloadOptions);
        } else {
          const result: { successCount: number; failCount: number } =
            yield documentsRepo.bulkDownloadWithFolders({
              documents: docsToDownload,
              folderIds: folders,
            });
          if (result.successCount > 0) {
            toast({
              title: t.common.success,
              description:
                result.failCount > 0
                  ? `${result.successCount} files downloaded, ${result.failCount} failed`
                  : `${result.successCount} files downloaded as zip`,
            });
          } else {
            // Empty-folder selection: the zip carries only directory structure.
            // failCount > 0 here means files the user explicitly picked were
            // dropped (e.g. a directly-picked doc whose owning folder is
            // trashed) — surface that rather than reporting plain success.
            toast({
              title: result.failCount > 0 ? t.common.warning : t.common.success,
              description:
                result.failCount > 0
                  ? `Folder structure downloaded as zip, ${result.failCount} file(s) failed`
                  : "Folder structure downloaded as zip",
            });
            if (result.failCount > 0 && folders.length === 0) return false;
          }
        }
        return true;
      } catch (e: any) {
        console.error("bulkDownload error", e);
        const errorMsg = e instanceof Error ? e.message : String(e);
        toast({
          title: t.common.error,
          description: `Failed to download: ${errorMsg}`,
          variant: "destructive",
        });
        return false;
      }
    }),

    /**
     * Bulk move selected documents to trash (soft delete).
     * When folderIds are provided, also trashes all documents inside those folders
     * (recursively including child folders) and removes the folder rows.
     *
     * Resolves `true` only when the trash actually ran. The abort paths
     * (nothing selected, failed selection lookup) resolve `false` rather than
     * rejecting, so callers MUST gate post-action cleanup — clearing the folder
     * selection, notifying the parent to refetch — on the result: a `.then()`
     * alone also fires on those and would silently drop the folder half of a
     * selection nothing was done to. A failure of the trash RPC itself rejects.
     */
    bulkMoveToTrash: flow(function* bulkMoveToTrash(
      folderIds?: string[],
      extraRows: SelectableDocument[] = []
    ) {
      const selectedIds = Array.from(self.selection.selectedIds.entries())
        .filter(([_, isSelected]) => isSelected)
        .map(([id]) => id);

      if (hasNothingToTrash(selectedIds, folderIds)) {
        warnNothingSelectedToTrash();
        return false;
      }

      // Resolve via the shared resolver (not just `self.documents`) so
      // timeline-selected rows resolve too, and so a doc picked on a page the
      // user has since left is recovered rather than skipped. `extraRows`
      // covers surfaces whose rows never enter the store (project-scoped
      // lists); `folderIds` keeps the DB folder cascade's descendants out of
      // the individual delete.
      let resolved: ResolvedSelection;
      try {
        resolved = yield resolveSelectionForAction(self, extraRows, folderIds ?? []);
      } catch (e) {
        // The recovery query failed, so we do NOT know whether the unresolved
        // ids still exist. Trashing the rest and clearing the selection would
        // tell the user those documents were gone on no evidence at all.
        reportSelectionLookupFailure(e);
        return false;
      }

      // Whatever is still unresolved after that round-trip is gone or
      // unreadable, so it is reported and skipped rather than trashed
      // sight-unseen.
      const resolvedIds = trashableIds(resolved);
      if (resolvedIds.length === 0 && !folderIds?.length) return false;

      const { userId } = resolveBulkActor(getRoot<BulkActorRoot>(self));

      try {
        // Issue attachments stay out of the trash (they go when their issue
        // lets go of them); each is named in its own toast and stays selected.
        const idsToTrash: string[] = yield withoutPinnedDocuments(resolvedIds, "trash");
        const keptIds = new Set(resolvedIds.filter((id) => !idsToTrash.includes(id)));
        if (idsToTrash.length === 0 && !folderIds?.length) return false;

        const result: { success: boolean; count: number } = yield documentsRepo.bulkMoveToTrash(
          idsToTrash,
          userId,
          folderIds
        );

        // Remove from store — the trashed rows AND the children the RPC
        // cascade-deleted server-side. The cascade set is collected across every
        // cached surface, not just the MST map: on the timeline a container's
        // children can exist only in the timeline caches, so a map-only walk
        // never finds them.
        const removedIds = [
          ...idsToTrash,
          ...collectCascadeDescendants(cascadeRowGroups(self), idsToTrash),
        ];
        removedIds.forEach((docId) => {
          self.documents.delete(docId);
        });
        // …and from the timeline's own row copies, which the MST map does not
        // back (same fan-out `applyRename` patches). Without this the trashed
        // cards stay on the timeline — the surface this bulk delete is for —
        // and keep resolving for re-selection until a full reload.
        removeTrashedRowsFromTimeline(self, removedIds);

        // `document.deleted` events are written by the repository, which sees
        // the folder cascade's rows this store never resolves.

        // Drop exactly what this trash consumed — the selection as it was when
        // the action was asked for, which covers the ids the folder cascade
        // handed to the RPC as well. A blanket clear() would also swallow a row
        // checked while the resolve round-trip was in flight, which was
        // deliberately left out of the delete.
        selectedIds
          .filter((id) => !keptIds.has(id))
          .forEach((id) => self.selection.selectedIds.delete(id));

        // Invalidate project document cache so pickers show fresh data
        invalidateProjectCacheState(self);

        // Show success toast
        const totalCount = result.count;
        toast({
          title: "Success",
          description: `Moved ${totalCount} document${totalCount === 1 ? "" : "s"} to trash`,
        });
        return true;
      } catch (e) {
        // A pinned file refused after the check (it was attached meanwhile):
        // named in its own toast, and nothing was trashed.
        if (notifyIfPinned(e)) return false;
        // Rethrows, so a mid-flight failure reaches the caller's .catch().
        reportBulkTrashFailure(e);
      }
    }),

    /**
     * Bulk restore selected documents from trash.
     */
    bulkRestore: flow(function* bulkRestore() {
      const selectedIds = Array.from(self.selection.selectedIds.entries())
        .filter(([_, isSelected]) => isSelected)
        .map(([id]) => id);

      if (selectedIds.length === 0) {
        toast({
          title: "No selection",
          description: "Please select documents to restore",
          variant: "destructive",
        });
        return;
      }

      try {
        yield documentsRepo.bulkRestore(selectedIds);

        // Remove from store (they'll appear in non-trash view now)
        selectedIds.forEach((docId) => {
          self.documents.delete(docId);
        });

        // `document.restored` events are written by the repository, off the
        // rows the server actually un-deleted — including the ones the folder
        // restore cascade brings back, which this store never resolves. It also
        // means a restore issued from anywhere else logs the same event.

        // Clear selection
        self.selection.selectedIds.clear();

        // Invalidate project document cache so pickers show fresh data
        invalidateProjectCacheState(self);

        // Show success toast
        toast({
          title: "Success",
          description: `Restored ${selectedIds.length} document${selectedIds.length === 1 ? "" : "s"}`,
        });
      } catch (e: any) {
        console.error("bulkRestore error", e);
        const errorMsg = e instanceof Error ? e.message : String(e);

        // Show error toast
        toast({
          title: "Error",
          description: `Failed to restore documents: ${errorMsg}`,
          variant: "destructive",
        });

        throw new Error(errorMsg);
      }
    }),

    /**
     * Bulk permanently delete selected documents.
     * WARNING: This action cannot be undone.
     */
    bulkPermanentDelete: flow(function* bulkPermanentDelete() {
      const selectedIds = Array.from(self.selection.selectedIds.entries())
        .filter(([_, isSelected]) => isSelected)
        .map(([id]) => id);

      if (selectedIds.length === 0) {
        toast({
          title: "No selection",
          description: "Please select documents to delete",
          variant: "destructive",
        });
        return;
      }

      try {
        yield documentsRepo.bulkPermanentDelete(selectedIds);

        // Remove from store
        selectedIds.forEach((docId) => {
          self.documents.delete(docId);
        });

        // Clear selection
        self.selection.selectedIds.clear();

        // Invalidate project document cache so pickers show fresh data
        invalidateProjectCacheState(self);

        // Show success toast
        toast({
          title: "Success",
          description: `Permanently deleted ${selectedIds.length} document${selectedIds.length === 1 ? "" : "s"}`,
        });
      } catch (e: any) {
        console.error("bulkPermanentDelete error", e);
        const errorMsg = e instanceof Error ? e.message : String(e);

        // Show error toast
        toast({
          title: "Error",
          description: `Failed to delete documents: ${errorMsg}`,
          variant: "destructive",
        });

        throw new Error(errorMsg);
      }
    }),

    /**
     * Subscribe to document changes for a building.
     * Uses granular event handling to update store incrementally without full reload.
     * Also fetches initial processing count and tracks it via WebSocket events.
     *
     * @param surface - Which view is subscribing, i.e. which surface a resync
     *   has to re-read. The mounted view is the only thing that knows this; the
     *   store's own navigation props can't be asked (see `activeSurface`).
     */
    subscribe(buildingId: string, projectId?: string | null, surface: DocumentSurface = "list") {
      self.activeSurface = surface;

      // Unsubscribe from existing channel if present
      if (self.channel) {
        documentsRepo.unsubscribeChannel(self.channel);
        self.channel = null;
      }
      if (self.visibilityHandler) {
        document.removeEventListener("visibilitychange", self.visibilityHandler);
        self.visibilityHandler = null;
      }

      // A tab that was in the background may have had its socket suspended and
      // missed every event since; nothing reports that, so treat coming back as
      // a resync trigger of its own.
      const onVisible = () => {
        if (document.visibilityState === "visible") this.revalidateVisibleDocuments(false);
      };
      document.addEventListener("visibilitychange", onVisible);
      self.visibilityHandler = onVisible;

      // Parent type is immutable for the lifetime of a realtime subscription,
      // and the parent event normally arrives before its extracted children.
      // Cache the answer (and any in-flight fallback lookup) so a large archive
      // extraction does not add one database round trip per child event.
      const signedParentLookups = new Map<number, Promise<boolean>>();
      const rememberParentType = (doc: { id: number; filename: string }) => {
        signedParentLookups.set(
          Number(doc.id),
          Promise.resolve(isSignedContainerFile(doc.filename))
        );
      };
      const isSignedParent = (parentId: number): Promise<boolean> => {
        const cached = signedParentLookups.get(parentId);
        if (cached) return cached;

        const parentInStore = self.documents.get(String(parentId));
        if (parentInStore) {
          const result = Promise.resolve(isSignedContainerFile(parentInStore.filename));
          signedParentLookups.set(parentId, result);
          return result;
        }

        const lookup = documentsRepo
          .filterSignedContainerParentIds([parentId])
          .then((signedIds) => {
            const isSigned = signedIds.has(parentId);
            // An empty degraded lookup may be a transient RLS/network failure.
            // Keep positive answers, but allow a later event to retry negatives
            // whose parent is not already in the store.
            if (!isSigned) signedParentLookups.delete(parentId);
            return isSigned;
          });
        signedParentLookups.set(parentId, lookup);
        return lookup;
      };

      // Fetch initial processing count and IDs for deduplication (scoped to the
      // active project when one is selected, matching the visible list/badge).
      documentsRepo
        .fetchProcessingDocumentIdsAndCount(buildingId, projectId)
        .then((result) => {
          this.setProcessingCountAndIds(result.count, result.ids);
        })
        .catch((e) => {
          console.error("Failed to fetch initial processing count", e);
        });

      // Subscribe to changes with granular event handling
      // ALL non-deleted documents are added to the store (not just COMPLETE)
      self.channel = documentsRepo.subscribeToChanges(buildingId, {
        onInsert: async (doc) => {
          if (!doc || doc.is_deleted) return;
          rememberParentType(doc);

          // Skip container documents once UNZIPPED (their children are visible instead)
          // But show containers during processing (CONTAINER, UNZIPPING states).
          // Signed containers are the exception — they stay the row, sealed
          // (same rule as listDocuments' applySignedContainerRowRule)...
          if (
            doc.container_type &&
            doc.state === "UNZIPPED" &&
            !isSignedContainerFile(doc.filename)
          )
            return;

          // ...and the children the pipeline unzips out of one are panel
          // content, not rows.
          if (doc.parent_document_id && (await isSignedParent(Number(doc.parent_document_id)))) {
            this.untrackProcessingDoc(String(doc.id));
            this.removeDocument(String(doc.id));
            return;
          }

          // Track new processing document (deduplicates via ID set)
          if (!COMPLETE_STATES.includes(doc.state)) {
            this.trackProcessingDoc(String(doc.id));
          }

          try {
            // Fetch full document data with relations
            const fullDoc = await documentsRepo.fetchSingleDocument(doc.id);
            if (!fullDoc) return;

            // Add ALL non-deleted documents to the store (processing + complete)
            this.addOrUpdateDocument(fullDoc);

            // Log document.uploaded event for COMPLETE docs
            if (COMPLETE_STATES.includes(doc.state)) {
              const docOrgId = doc.org_id;
              if (docOrgId) {
                eventLogger.log({
                  event_type: "document.uploaded",
                  entity_type: "document",
                  entity_id: String(fullDoc.id),
                  org_id: docOrgId,
                  metadata: {
                    filename: fullDoc.filename,
                    fileSize: fullDoc.size,
                    contentType: fullDoc.content_type,
                    buildingId: fullDoc.building_id,
                    projectId: fullDoc.project_id,
                    uploadedBy: fullDoc.created_by,
                  },
                });
              }
            }
          } catch (e) {
            // Error handled silently - document will appear on next refresh
          }
        },

        onUpdate: async (oldDoc, newDoc) => {
          if (!newDoc) return;
          rememberParentType(newDoc);

          const docId = String(newDoc.id);
          const isDeleted = newDoc.is_deleted;
          const wasDeleted = oldDoc?.is_deleted;

          // An enclosed child is panel content, never a list row. Resolve this
          // before processing tracking so inserts and updates count it the same,
          // and remove a stale row that a degraded earlier query may have loaded.
          if (
            newDoc.parent_document_id &&
            (await isSignedParent(Number(newDoc.parent_document_id)))
          ) {
            this.untrackProcessingDoc(docId);
            this.removeDocument(docId);
            return;
          }

          // A row that changed project — a cross-project move, including a
          // lineage moved in as new versions of another document — is swept
          // only from its NEW project's slots further down. Its old project's
          // folder slots would otherwise keep offering it until they expire.
          const oldProjectId = oldDoc?.project_id;
          if (oldProjectId != null && String(oldProjectId) !== String(newDoc.project_id ?? "")) {
            this.evictFromProjectCache(String(oldProjectId), docId);
          }

          // Track/untrack processing count based on current state
          const isProcessing = !COMPLETE_STATES.includes(newDoc.state);
          if (isProcessing && !isDeleted) {
            this.trackProcessingDoc(docId);
          } else {
            this.untrackProcessingDoc(docId);
          }

          // Case 1: Document was deleted (soft delete) → remove from store
          if (!wasDeleted && isDeleted) {
            this.removeDocument(docId);
            return;
          }

          // Case 2: Container became UNZIPPED → remove (children are now visible).
          // A signed container is the exception: it stays visible as the row,
          // so it falls through to Case 3 and refreshes in place.
          if (
            newDoc.container_type &&
            newDoc.state === "UNZIPPED" &&
            !isSignedContainerFile(newDoc.filename)
          ) {
            this.removeDocument(docId);
            return;
          }

          // Case 3: Any other non-deleted update → fetch fresh and update store
          if (!isDeleted) {
            try {
              const fullDoc = await documentsRepo.fetchSingleDocument(newDoc.id);
              if (fullDoc) {
                this.addOrUpdateDocument(fullDoc);

                // Log document.uploaded event when transitioning to COMPLETE
                const wasComplete = oldDoc && COMPLETE_STATES.includes(oldDoc.state);
                const isComplete = COMPLETE_STATES.includes(newDoc.state);
                if (!wasComplete && isComplete) {
                  const docOrgId = newDoc.org_id;
                  if (docOrgId) {
                    eventLogger.log({
                      event_type: "document.uploaded",
                      entity_type: "document",
                      entity_id: String(fullDoc.id),
                      org_id: docOrgId,
                      metadata: {
                        filename: fullDoc.filename,
                        fileSize: fullDoc.size,
                        contentType: fullDoc.content_type,
                        buildingId: fullDoc.building_id,
                        projectId: fullDoc.project_id,
                        uploadedBy: fullDoc.created_by,
                      },
                    });
                  }
                }
              }
            } catch (e) {
              console.error("Failed to fetch updated document", e);
            }
          }
        },

        onDelete: (doc) => {
          // Hard delete - remove from store and untrack from processing
          if (doc) {
            const docId = String(doc.id);
            this.untrackProcessingDoc(docId);
            this.removeDocument(docId);
          }
        },

        // The channel re-joined, so events fired while it was down were never
        // delivered and never will be. Everything the store holds was built
        // from those events; re-read it.
        onResync: () => {
          this.revalidateVisibleDocuments(true);
        },
      });
    },

    /**
     * Add or update a document in the store.
     * Used by realtime subscription for incremental updates.
     * Also syncs with projectDocuments cache for project page realtime updates.
     */
    addOrUpdateDocument(doc: any) {
      const docId = String(doc.id);

      // A superseded version (is_latest_version === false) must never live in
      // the store. Uploading a new version inserts a NEW row (new id) as the
      // latest and flips this row's is_latest_version to false; the flip
      // arrives here as its own realtime UPDATE. If we re-added the row it would
      // resurface the old version's row and tags in both the documents and
      // project lists (the reported bug). Drop it from the main map and the
      // project cache, then stop. (Only the realtime path selects
      // is_latest_version, so this is `undefined` — never `false` — elsewhere.)
      if (doc.is_latest_version === false) {
        this.removeDocument(docId);
        // removeDocument can only reach the project cache when the row is in the
        // main map; in project-filtered views it lives only in the cache, so
        // drop it there explicitly too (a no-op when already gone).
        this.syncToProjectCache(doc);
        return;
      }

      const isExistingDocument = self.documents.has(docId);

      // When a project filter is active, only add documents that match the filter
      // to the main documents map. This prevents realtime events for other projects'
      // documents from polluting the filtered view (WHA-2927).
      // Always allow updates to existing documents (they were already in the view).
      const activeProjectIds = self.currentFilters?.projectIds;
      if (
        !isExistingDocument &&
        activeProjectIds &&
        activeProjectIds.length > 0 &&
        !activeProjectIds.includes(doc.project_id)
      ) {
        // Document doesn't match current project filter — skip adding to main map
        // but still sync to project cache below
        this.syncToProjectCache(doc);
        return;
      }

      self.documents.set(
        docId,
        DocumentModel.create({
          id: docId,
          filename: doc.filename,
          file_path: doc.file_path,
          building_id: String(doc.building_id),
          parent_document_id: doc.parent_document_id ? String(doc.parent_document_id) : null,
          content_type: doc.content_type,
          size: doc.size,
          created_at: doc.created_at,
          original_created_at: doc.original_created_at ?? null,
          modified_at: doc.modified_at ?? null,
          document_content_modified_date: doc.document_content_modified_date ?? null,
          effective_modified_at: doc.effective_modified_at ?? null,
          created_by: doc.created_by,
          created_by_name: doc.created_by_name ?? null,
          created_by_avatar_url: doc.created_by_avatar_url ?? null,
          created_by_color: doc.created_by_color ?? null,
          state: doc.state ?? null,
          version: doc.version ?? null,
          version_group: doc.version_group ?? null,
          source: doc.source ?? null,
          container_type: doc.container_type ?? null,
          project_id: doc.project_id ?? null,
          folder_id: doc.folder_id ? String(doc.folder_id) : null,
          is_deleted: doc.is_deleted ?? false,
          approved: doc.approved ?? false,
          verified_at: doc.verified_at ?? null,
          verified_by: doc.verified_by ?? null,
          verified_by_name: doc.verified_by_name ?? null,
          categories: doc.categories ?? [],
          design_disciplines: doc.design_disciplines ?? [],
          design_stages: doc.design_stages ?? [],
        })
      );

      // Update total count if this is a new document - increment by 1
      // (Don't set to documents.size as that only reflects the cached portion)
      if (!isExistingDocument) {
        self.totalCount = self.totalCount + 1;
      }

      // Sync to project cache: remove from old folder, update in new folder
      if (doc.project_id) {
        const projectId = String(doc.project_id);
        const projectCache = self.projectDocuments.get(projectId);
        if (!projectCache) return; // No cache for this project yet — nothing to sync

        const folderKey = doc.folder_id ? String(doc.folder_id) : "root";
        const versionGroup: string | null = doc.version_group ?? null;
        const isLatest: boolean = doc.is_latest_version !== false;

        // Remove document from any OTHER folder caches (it may have moved)
        for (const [key, docs] of projectCache.entries()) {
          if (key === folderKey) continue;
          const idx = (docs as any[]).findIndex((d: any) => String(d.id) === docId);
          if (idx >= 0) {
            const updated = [...(docs as any[])];
            updated.splice(idx, 1);
            projectCache.set(key, updated);
            claimPatchedSlot(self, projectId, key);
          }
        }

        // Only update existing folder cache entries — don't create new ones
        if (projectCache.has(folderKey)) {
          let folderDocs = [...(projectCache.get(folderKey)! as any[])];

          // Doc is no longer the latest version of its group → drop from cache.
          // (Without this, stale v1 rows linger after v2 is uploaded.)
          if (!isLatest) {
            folderDocs = folderDocs.filter((d) => String(d.id) !== docId);
            projectCache.set(folderKey, folderDocs);
            claimPatchedSlot(self, projectId, folderKey);
            self.projectCacheVersion += 1;
            return;
          }

          // Latest version arrived → evict any older siblings in the same
          // version_group from this folder's cache. Keeps the list view
          // showing one row per version_group even while the pipeline is
          // still firing UPDATE events on the new version.
          if (versionGroup) {
            folderDocs = folderDocs.filter(
              (d) => d.version_group !== versionGroup || String(d.id) === docId
            );
          }

          const existingIndex = folderDocs.findIndex((d: any) => String(d.id) === docId);
          const newDocs =
            existingIndex >= 0
              ? [...folderDocs.slice(0, existingIndex), doc, ...folderDocs.slice(existingIndex + 1)]
              : [doc, ...folderDocs];
          projectCache.set(folderKey, newDocs);
          claimPatchedSlot(self, projectId, folderKey);
        }
        self.projectCacheVersion += 1;
      }
    },

    /**
     * Sync a document to the project cache (for realtime updates).
     * This is a separate action so it can be called from async realtime handlers.
     */
    syncToProjectCache(doc: any) {
      if (!doc?.project_id) return;

      const projectId = String(doc.project_id);
      const projectCache = self.projectDocuments.get(projectId);
      if (!projectCache) return; // No cache for this project yet — nothing to sync

      const docId = String(doc.id);
      const folderKey = doc.folder_id ? String(doc.folder_id) : "root";
      const versionGroup: string | null = doc.version_group ?? null;
      const isLatest: boolean = doc.is_latest_version !== false;

      // Remove document from any OTHER folder caches (it may have moved)
      for (const [key, docs] of projectCache.entries()) {
        if (key === folderKey) continue;
        const idx = (docs as any[]).findIndex((d: any) => String(d.id) === docId);
        if (idx >= 0) {
          const updated = [...(docs as any[])];
          updated.splice(idx, 1);
          projectCache.set(key, updated);
          claimPatchedSlot(self, projectId, key);
        }
      }

      // Only update existing folder cache entries — don't create new ones
      if (projectCache.has(folderKey)) {
        let folderDocs = [...(projectCache.get(folderKey)! as any[])];

        // If this incoming doc is no longer the latest version of its group,
        // drop it from the cache entirely. The UI should only render latest
        // versions; the old entry (if any) is stale.
        if (!isLatest) {
          folderDocs = folderDocs.filter((d) => String(d.id) !== docId);
          projectCache.set(folderKey, folderDocs);
          claimPatchedSlot(self, projectId, folderKey);
          self.projectCacheVersion += 1;
          return;
        }

        // Incoming doc IS the latest version. Drop any OTHER docs in the same
        // version_group from this folder's cache — they're stale previous
        // versions. This is what collapses v1 out of the list when v2 arrives
        // (either via the RPC's is_latest_version flip or a fresh INSERT).
        if (versionGroup) {
          folderDocs = folderDocs.filter(
            (d) => d.version_group !== versionGroup || String(d.id) === docId
          );
        }

        const existingIndex = folderDocs.findIndex((d: any) => String(d.id) === docId);

        let newDocs: any[];
        if (existingIndex >= 0) {
          newDocs = [...folderDocs];
          newDocs[existingIndex] = doc;
        } else {
          newDocs = [doc, ...folderDocs];
        }

        projectCache.set(folderKey, newDocs);
        claimPatchedSlot(self, projectId, folderKey);
      }
      self.projectCacheVersion += 1;
    },

    /**
     * Refresh a single document from the server.
     * Used after local updates (like Teach Franklin) to get authoritative data
     * without reloading all documents.
     */
    refreshSingleDocument: flow(function* refreshSingleDocument(docId: number) {
      try {
        const fullDoc: Awaited<ReturnType<typeof documentsRepo.fetchSingleDocument>> =
          yield documentsRepo.fetchSingleDocument(docId);
        if (fullDoc) {
          // Use the existing addOrUpdateDocument to update the store
          // @ts-expect-error - self refers to instance in MST actions
          self.addOrUpdateDocument(fullDoc);
        }
      } catch (e) {
        console.error("Failed to refresh document", docId, e);
        // Don't throw - this is a non-critical refresh operation
      }
    }),

    /**
     * Remove a document from the store.
     * Used by realtime subscription for incremental updates.
     * Also removes from projectDocuments cache for project page realtime updates.
     */
    /**
     * Drop one document from every folder slot of one project's cache. Used
     * when a realtime update shows the row LEFT that project: the regular sync
     * only looks at the project the row is in now.
     */
    evictFromProjectCache(projectId: string, docId: string) {
      const projectCache = self.projectDocuments.get(projectId);
      if (!projectCache) return;
      let patched = false;
      for (const [key, docs] of projectCache.entries()) {
        if (!(docs as any[]).some((d: any) => String(d.id) === docId)) continue;
        projectCache.set(
          key,
          (docs as any[]).filter((d: any) => String(d.id) !== docId)
        );
        claimPatchedSlot(self, projectId, key);
        patched = true;
      }
      if (patched) self.projectCacheVersion += 1;
    },

    removeDocument(docId: string) {
      // Get document info before removing (for project cache sync)
      const doc = self.documents.get(docId);
      const projectId = doc?.project_id ? String(doc.project_id) : null;
      const folderId = doc?.folder_id ? String(doc.folder_id) : null;

      if (self.documents.has(docId)) {
        self.documents.delete(docId);
        self.selection.selectedIds.delete(docId);
        self.totalCount = Math.max(0, self.totalCount - 1);
      }

      // Sync with projectDocuments cache for realtime updates on project pages
      if (projectId) {
        const projectCache = self.projectDocuments.get(projectId);
        if (projectCache) {
          const folderKey = folderId || "root";
          const folderDocs = projectCache.get(folderKey);
          if (folderDocs) {
            const index = folderDocs.findIndex((d: any) => String(d.id) === docId);
            if (index >= 0) {
              // Create new array without the document (for MobX reactivity)
              const newDocs = folderDocs.filter((_: any, i: number) => i !== index);
              projectCache.set(folderKey, newDocs);
              claimPatchedSlot(self, projectId, folderKey);
              // Increment cache version to trigger MobX reactivity
              self.projectCacheVersion += 1;
            }
          }
        }
      }
    },

    /**
     * Re-read whatever the user is currently looking at, straight from the
     * database.
     *
     * Both document surfaces are maintained incrementally from realtime events
     * once loaded, so any gap in that stream leaves them quietly wrong — most
     * visibly by keeping a superseded version's row as the lineage's anchor
     * after a newer version was uploaded elsewhere. Called when the realtime
     * channel re-joins (a gap definitely happened) and when the tab becomes
     * visible again (one probably did).
     *
     * Expiring the slot rather than deleting it keeps the current rows on
     * screen while the fetch is in flight — the loaders swap them atomically,
     * so nothing flashes and the scroll position holds.
     *
     * Which surface to re-read comes from `activeSurface` — the view that
     * subscribed — never from `currentProjectId`, which survives the Folders
     * tab it was set on and would otherwise send every later List/Timeline
     * resync into a folder slot nothing is rendering.
     */
    revalidateVisibleDocuments(force = false) {
      if (self.activeSurface === "folders") {
        const projectId = self.currentProjectId;
        // Subscribed before the view's first load landed: there is nothing on
        // screen to re-read, and that load is itself a fresh read.
        if (!projectId) return;

        const folderId = self.currentFolderId;
        const metaKey = `${projectId}|${folderId || "root"}`;
        const meta = self.projectFolderSortMeta.get(metaKey);
        // Forced: expire the slot so the loader cannot serve it. Otherwise let
        // the loader's own freshness check decide, so alt-tabbing does not
        // refetch on every switch.
        if (force && meta) self.projectFolderSortMeta.set(metaKey, { ...meta, fetchedAt: 0 });

        const reload = folderId
          ? this.loadFolderDocuments(projectId, folderId, meta?.sortKey, meta?.sortOrder)
          : this.loadProjectRootDocuments(projectId, meta?.sortKey, meta?.sortOrder);
        reload.catch((e: unknown) => {
          console.error("[DocumentStore] Failed to revalidate project documents", e);
        });
        return;
      }

      if (self.activeSurface === "timeline") {
        // Timeline rows come from server-side aggregations that re-run only on
        // a filter or zoom change, so realtime events never reached them: a
        // just-uploaded document was simply absent, and state badges never
        // advanced. Same TTL discipline as the list.
        if (!force && Date.now() - self.timelineFetchedAt < PROJECT_DOC_CACHE_TTL_MS) return;
        // Subscribed before the view registered its re-read — that first load
        // is itself a fresh read, so there is nothing to do.
        self.timelineRevalidate?.();
        return;
      }

      // Building-wide list: re-fetch the page the user is on, not page 1.
      const buildingId = self.currentBuildingId;
      if (!buildingId) return;
      if (!force && Date.now() - self.documentsFetchedAt < PROJECT_DOC_CACHE_TTL_MS) return;
      this.goToPage(buildingId, self.currentPage).catch((e: unknown) => {
        console.error("[DocumentStore] Failed to revalidate document list", e);
      });
    },

    /**
     * Register (or clear, with null) the Timeline's re-read. The view owns its
     * own filter and zoom state, so it supplies the closure.
     */
    setTimelineRevalidate(fn: (() => void) | null) {
      self.timelineRevalidate = fn;
    },

    /**
     * Unsubscribe from document changes.
     */
    unsubscribe() {
      if (self.channel) {
        documentsRepo.unsubscribeChannel(self.channel);
        self.channel = null;
      }
      if (self.visibilityHandler) {
        document.removeEventListener("visibilitychange", self.visibilityHandler);
        self.visibilityHandler = null;
      }
    },

    /**
     * Rename a folder in the tag map.
     *
     * Document rows and the timeline's breadcrumb read their folder labels from
     * this map, which is only (re)filled by loadTags — so without this a renamed
     * folder kept its old label until the page was reloaded.
     *
     * `folder_path` is rewritten here too, for this folder and every descendant
     * whose path runs through it: this map holds rows read before the rename, so
     * anything that falls back to a path (a breadcrumb segment for an ancestor
     * that predates the `name` column, the IFC model picker's folder tooltip)
     * would otherwise still render the old name. The database keeps its own copy
     * correct — franklin-data-pipeline `update_folder_metadata` computes the
     * row's path from the post-update record, and
     * `refresh_descendant_folder_paths` re-paths the subtree — but none of that
     * reaches an in-memory map only loadTags refills.
     */
    renameFolderTag(folderId: string, name: string) {
      const folder = self.tags.folders.get(String(folderId));
      if (!folder) return;

      folder.name = name;

      // The prefix comes from the folder's own stored path, never the parent's:
      // the parent can be absent from this map (fetchFolders filters hidden
      // folders and scopes to one project), and a missing parent would collapse
      // the folder — and every descendant — to a root-level path. A folder with
      // no stored path has nothing stale to rewrite; name-based rendering is
      // already correct.
      const previousPath = folder.folder_path;
      if (!previousPath) return;
      const nextPath = previousPath.slice(0, previousPath.lastIndexOf("/") + 1) + name;
      folder.folder_path = nextPath;

      if (previousPath === nextPath) return;
      self.tags.folders.forEach((descendant) => {
        if (descendant.folder_path?.startsWith(`${previousPath}/`)) {
          descendant.folder_path = nextPath + descendant.folder_path.slice(previousPath.length);
        }
      });
    },

    /**
     * Re-parent a folder in the tag map — the move counterpart of
     * renameFolderTag, for the same reason: only loadTags refills this map, so
     * without it a moved folder's chip and breadcrumb keep the old ancestor
     * chain until a full page reload.
     *
     * A move under a parent that is missing from the map (hidden or
     * out-of-scope) makes the new chain unknowable — the stored paths are
     * cleared rather than left stale, so consumers fall back to the name chain.
     */
    moveFolderTag(folderId: string, newParentId: string | null) {
      const folder = self.tags.folders.get(String(folderId));
      if (!folder) return;

      const previousPath = folder.folder_path;
      folder.parentId = newParentId ? String(newParentId) : null;

      const parent = newParentId ? self.tags.folders.get(String(newParentId)) : null;
      const nextPath = !newParentId
        ? `/${folder.name}`
        : parent?.folder_path
          ? `${parent.folder_path}/${folder.name}`
          : null;
      folder.folder_path = nextPath;

      if (!previousPath || previousPath === nextPath) return;
      self.tags.folders.forEach((descendant) => {
        if (descendant.folder_path?.startsWith(`${previousPath}/`)) {
          descendant.folder_path =
            nextPath == null ? null : nextPath + descendant.folder_path.slice(previousPath.length);
        }
      });
    },

    /**
     * Load tags (projects, folders, custom tags) for a building.
     */
    loadTags: flow(function* loadTags(buildingId: string, projectId?: string | null) {
      const seq = ++self.tagsRequestSeq;
      self.tags.isLoading = true;
      self.tags.error = null;

      try {
        // Projects stay building-wide (a name lookup map); folders + custom tags
        // narrow to the scoped project so their chips don't include other projects.
        const [projects, folders, customTags]: [any[], any[], string[]] = yield Promise.all([
          documentsRepo.fetchProjects(buildingId),
          documentsRepo.fetchFolders(buildingId, projectId),
          documentsRepo.fetchCustomTags(buildingId, projectId),
        ]);

        // A newer call (a project switch, or the realtime refresh racing one)
        // owns these maps now — writing this scope's answer over it is exactly
        // the wrong-project folder list the seq exists to prevent.
        if (seq !== self.tagsRequestSeq) return;

        // Replaced in one assignment rather than cleared and refilled: `replace`
        // reconciles by key, so a re-load keeps the nodes renameFolderTag /
        // moveFolderTag have already patched, and no observer ever sees the
        // empty map a clear-then-fill leaves behind.
        self.tags.projects.replace(
          new Map(projects.map((project) => [String(project.id), project]))
        );
        self.tags.folders.replace(new Map(folders.map((folder) => [String(folder.id), folder])));
        self.tags.customTags.replace(customTags);
      } catch (e: any) {
        console.error("loadTags error", e);
        // Only the newest request's failure is the one on screen; a superseded
        // scope's error would otherwise sit next to the newer scope's values.
        if (seq === self.tagsRequestSeq)
          self.tags.error = e instanceof Error ? e.message : String(e);
        throw e;
      } finally {
        if (seq === self.tagsRequestSeq) self.tags.isLoading = false;
      }
    }),

    /**
     * Load root documents for a project (documents with folder_id IS NULL).
     * Results are cached by projectId with "root" as the folder key.
     *
     * @param projectId - The project ID to load root documents for
     * @returns Array of documents from cache or freshly fetched
     */
    loadProjectRootDocuments: flow(function* loadProjectRootDocuments(
      projectId: string,
      sortKey?: SortKey,
      sortOrder?: SortOrder
    ) {
      self.isLoadingProjectDocs = true;
      self.currentProjectId = projectId;
      // Don't update currentFolderId yet - keep showing current folder's docs during load
      // to prevent scroll position reset from empty content
      self.error = null;

      const metaKey = `${projectId}|root`;
      // Record the sort this call is asking for, so a peek fetches the order
      // the view is switching to rather than the one the slot still holds (see
      // projectFolderPendingSort). Claimed BEFORE the cache check, not after
      // it: a hit returns without claiming anything, so a claim left by an
      // earlier request — a sort change that failed, say — would outlive the
      // sort it named and send the next peek after an order no view asked for.
      self.projectFolderPendingSort.set(metaKey, { sortKey, sortOrder });
      // Claim the shared loading flag: the newest loader owns it, so an
      // overtaken response cannot turn the spinner off while the response that
      // will actually be rendered is still on the wire.
      const loadSeq = ++self.projectDocsLoadSeq;

      try {
        // Check cache first. The cache slot is keyed by folder; we use a
        // sibling map (projectFolderSortMeta) to remember which sort the slot
        // was last fetched with, and when. Cache hit only when the slot exists,
        // the sort matches AND the slot is still fresh
        // (PROJECT_DOC_CACHE_TTL_MS — realtime events are not guaranteed, so a
        // slot may only be trusted for so long). Otherwise we fall through to
        // the fetch path WITHOUT clearing the cache — so the previous rows
        // remain visible under the loading overlay and the page scroll
        // position doesn't collapse while the fresh ones are on the wire.
        const projectCache = self.projectDocuments.get(projectId);
        const cached = projectCache?.get("root");
        const cachedMeta = self.projectFolderSortMeta.get(metaKey);
        const sortMatches = cachedMeta?.sortKey === sortKey && cachedMeta?.sortOrder === sortOrder;
        const isFresh =
          cachedMeta != null && Date.now() - cachedMeta.fetchedAt < PROJECT_DOC_CACHE_TTL_MS;
        if (cached && sortMatches && isFresh) {
          // Only update currentFolderId after we have data to show
          self.currentFolderId = null;
          self.isLoadingProjectDocs = false;
          return cached;
        }

        // Claim the slot for this request, so a response that a later reader
        // of the same slot has already overtaken cannot write it (see
        // projectFolderRequestSeq).
        const seq = (self.projectFolderRequestSeq.get(metaKey) ?? 0) + 1;
        self.projectFolderRequestSeq.set(metaKey, seq);

        // Fetch from repository — refetching if an invalidation lands while
        // these rows are on the wire.
        const { docs, isSuperseded } = yield* fetchOutlastingInvalidations(
          self,
          {
            projectId,
            folderId: null, // null = root documents
            sortKey,
            sortOrder,
          },
          () => self.projectFolderRequestSeq.get(metaKey) !== seq
        );

        // Cache results — atomically replace the slot. Any previously displayed
        // (stale-sort) rows for this folder are swapped out only here, after
        // the network round-trip has completed. Overtaken while in flight: the
        // slot already holds newer rows for this same slot, and writing these
        // would also re-stamp them fresh for the full TTL.
        if (!isSuperseded) {
          commitProjectCacheSlot(self, projectId, "root", docs, { sortKey, sortOrder });
        }

        // Only update currentFolderId AFTER docs are cached and ready to
        // display — this prevents the document list from showing empty during
        // load. Committed even when overtaken: whatever overtook this request
        // is another read of the very same slot, so the open folder it names
        // is the one this request opened.
        self.currentFolderId = null;

        return docs;
      } catch (e: any) {
        console.error("loadProjectRootDocuments error", e);
        self.error = e instanceof Error ? e.message : String(e);
        releasePendingSortClaim(self.projectFolderPendingSort, metaKey, sortKey, sortOrder);
        // On error, still update folderId to show the empty state
        self.currentFolderId = null;
        // `null`, not an empty list: nothing was cached, so a caller handed
        // `[]` cannot tell a genuinely empty root from a read that failed —
        // and it can't learn it from a rejection either, because this catch
        // is what stops the flow from rejecting. Rethrowing instead would
        // hand every other caller an unhandled rejection, so the failure
        // leaves by the same door as the success. See the navigation effect
        // in ProjectScopedFolderView, which commits the open folder on this
        // result and must not commit one whose documents never arrived.
        return null;
      } finally {
        if (self.projectDocsLoadSeq === loadSeq) self.isLoadingProjectDocs = false;
      }
    }),

    /**
     * Load documents for a specific folder within a project.
     * Results are cached by projectId/folderId combination.
     *
     * @param projectId - The project ID
     * @param folderId - The folder ID to load documents for
     * @returns Array of documents from cache or freshly fetched
     */
    loadFolderDocuments: flow(function* loadFolderDocuments(
      projectId: string,
      folderId: string,
      sortKey?: SortKey,
      sortOrder?: SortOrder
    ) {
      self.isLoadingProjectDocs = true;
      self.currentProjectId = projectId;
      // Don't update currentFolderId yet - keep showing old folder's docs during load
      // to prevent scroll position reset from empty content
      self.error = null;

      const metaKey = `${projectId}|${folderId}`;
      // Recorded before the cache check, for the reason spelled out in
      // loadProjectRootDocuments: a hit returns without claiming anything, so a
      // claim an earlier request left behind would outlive the sort it named.
      self.projectFolderPendingSort.set(metaKey, { sortKey, sortOrder });
      // Owns the loading flag until a newer loader claims it — see
      // loadProjectRootDocuments.
      const loadSeq = ++self.projectDocsLoadSeq;

      try {
        // Cache hit only when the slot exists, the sort matches AND the slot
        // is still fresh (PROJECT_DOC_CACHE_TTL_MS — realtime events are not
        // guaranteed, so a slot may only be trusted for so long). Otherwise we
        // fall through to the fetch path WITHOUT clearing the cache, so the
        // rows already on screen stay visible under the loading overlay until
        // the fresh ones arrive. See the analogous comment on
        // loadProjectRootDocuments above.
        const projectCache = self.projectDocuments.get(projectId);
        const cached = projectCache?.get(folderId);
        const cachedMeta = self.projectFolderSortMeta.get(metaKey);
        const sortMatches = cachedMeta?.sortKey === sortKey && cachedMeta?.sortOrder === sortOrder;
        const isFresh =
          cachedMeta != null && Date.now() - cachedMeta.fetchedAt < PROJECT_DOC_CACHE_TTL_MS;
        if (cached && sortMatches && isFresh) {
          // Only update currentFolderId after we have data to show
          self.currentFolderId = folderId;
          self.isLoadingProjectDocs = false;
          return cached;
        }

        // Claim the slot for this request, so a response that a later reader
        // of the same slot has already overtaken cannot write it (see
        // projectFolderRequestSeq).
        const seq = (self.projectFolderRequestSeq.get(metaKey) ?? 0) + 1;
        self.projectFolderRequestSeq.set(metaKey, seq);

        // Fetch from repository — refetching if an invalidation lands while
        // these rows are on the wire.
        const { docs, isSuperseded } = yield* fetchOutlastingInvalidations(
          self,
          { projectId, folderId, sortKey, sortOrder },
          () => self.projectFolderRequestSeq.get(metaKey) !== seq
        );

        // Cache results — atomically replace the slot. The view re-reads from
        // cache on the projectCacheVersion bump and transitions from the
        // previous sort to the new sort in a single render. Overtaken while in
        // flight: the slot already holds newer rows, and writing these would
        // also re-stamp them fresh for the full TTL.
        if (!isSuperseded) {
          commitProjectCacheSlot(self, projectId, folderId, docs, { sortKey, sortOrder });
        }

        // Only update currentFolderId AFTER docs are cached and ready to
        // display — this prevents the document list from showing empty during
        // load. Committed even when overtaken: the request that overtook this
        // one reads the very same slot, so it opens the same folder.
        self.currentFolderId = folderId;

        return docs;
      } catch (e: any) {
        console.error("loadFolderDocuments error", e);
        self.error = e instanceof Error ? e.message : String(e);
        releasePendingSortClaim(self.projectFolderPendingSort, metaKey, sortKey, sortOrder);
        // On error, still update folderId to show the empty state
        self.currentFolderId = folderId;
        // `null` rather than an empty list — an empty folder and a folder
        // whose documents could not be read look identical otherwise. See the
        // matching comment in loadProjectRootDocuments above.
        return null;
      } finally {
        if (self.projectDocsLoadSeq === loadSeq) self.isLoadingProjectDocs = false;
      }
    }),

    /**
     * Fetch documents for a project/folder, using the cache when available.
     * Unlike loadFolderDocuments, this does NOT change currentProjectId or
     * currentFolderId, making it safe for picker dialogs that shouldn't
     * affect the main view's navigation state.
     */
    peekFolderDocuments: flow(function* peekFolderDocuments(
      projectId: string,
      folderId: string | null,
      options?: { fresh?: boolean }
    ) {
      const cacheKey = folderId ?? "root";
      const metaKey = `${projectId}|${cacheKey}`;
      // A peek has no sort of its own, and the slot it writes is the one the
      // folder view renders verbatim (sorting is server-side). Fetching
      // sortless would fetch `created_at desc` and re-sort that table the
      // moment a picker opened over it, so the peek asks for the sort the view
      // last asked for — the pending claim of a sort change still in flight,
      // whose loader this peek is about to overtake, and otherwise the sort the
      // slot was filled with. Either way the rows it writes back stay in the
      // order the view is showing (see projectFolderPendingSort).
      const meta = self.projectFolderSortMeta.get(metaKey);
      const pendingSort = self.projectFolderPendingSort.get(metaKey);
      const sort = pendingSort ?? { sortKey: meta?.sortKey, sortOrder: meta?.sortOrder };

      if (!options?.fresh) {
        const projectCache = self.projectDocuments.get(projectId);
        const cached = projectCache?.get(cacheKey);
        // The loaders' freshness window, for the same reason they have one:
        // realtime delivery is not guaranteed, so a slot may only be trusted
        // for so long.
        const isFresh = meta != null && Date.now() - meta.fetchedAt < PROJECT_DOC_CACHE_TTL_MS;
        if (cached && isFresh) return cached;
      }

      // Claim the slot, so a response a later reader has already overtaken
      // cannot write it. Racing for one slot is routine here: a `fresh` reader
      // goes to the network whatever is cached, and the folder view's own
      // loader is reading the same slot behind the dialog — the older response
      // landing last would put rows back that a newer one had already
      // replaced, superseded version and all. One counter covers all of them
      // (see projectFolderRequestSeq); the loaders' spinner ownership is
      // tracked separately, so claiming this cannot strand it.
      const seq = (self.projectFolderRequestSeq.get(metaKey) ?? 0) + 1;
      self.projectFolderRequestSeq.set(metaKey, seq);

      const { docs, isSuperseded } = yield* fetchOutlastingInvalidations(
        self,
        { projectId, folderId, ...sort },
        () => self.projectFolderRequestSeq.get(metaKey) !== seq
      );
      if (isSuperseded) return docs;

      // Written with the sort they were actually fetched in, so a loader may
      // serve them as the fresh hit they are.
      commitProjectCacheSlot(self, projectId, cacheKey, docs, sort);
      return docs;
    }),

    /**
     * Invalidate the project document cache.
     * If projectId is provided, only that project's cache is cleared.
     * If no projectId is provided, all project caches are cleared.
     *
     * @param projectId - Optional project ID to clear cache for
     */
    invalidateProjectCache(projectId?: string) {
      invalidateProjectCacheState(self, projectId);
    },

    /**
     * Apply a rename to in-memory caches synchronously after a successful
     * API rename, so observer-based rows re-render with the new filename
     * without waiting for a refetch. (WHA-3259)
     *
     * Every view keeps its own copy of the row, so all of them have to be
     * patched here — this is the single update path each rename handler calls:
     *  - the MST documents map (list view, /documents)
     *  - the projectDocuments cache (folder view, /projects)
     *  - the timeline's row copies: expanded clusters (cluster + Days view),
     *    the max-zoom individual list (both the merged display list and the
     *    server list it re-merges from), and the content-match rows those
     *    merges draw on. Patching only the first two left the timeline showing
     *    the old name until the page was reloaded.
     */
    applyRename(docId: string, newFilename: string) {
      const model = self.documents.get(docId);
      if (model) model.setFilename(newFilename);

      let patched = false;
      for (const [projectId, projectCache] of self.projectDocuments.entries()) {
        for (const [folderKey, docs] of projectCache.entries()) {
          const arr = docs as any[];
          const idx = arr.findIndex((d) => String(d.id) === docId);
          if (idx >= 0) {
            const next = [...arr];
            next[idx] = { ...next[idx], filename: newFilename };
            projectCache.set(folderKey, next);
            // The rename is already committed server-side, so a fetch that
            // left before it would put the old name back (see
            // claimPatchedSlot).
            claimPatchedSlot(self, projectId, folderKey);
            patched = true;
          }
        }
      }
      if (patched) self.projectCacheVersion += 1;

      for (const cluster of self.expandedClusters.values()) {
        const next = renameInRows(cluster.documents, docId, newFilename);
        if (next) cluster.setDocumentRows(next);
      }

      const nextIndividual = renameInRows(self.timelineIndividualDocs, docId, newFilename);
      if (nextIndividual) self.timelineIndividualDocs = nextIndividual;

      const nextIndividualServer = renameInRows(
        self.timelineIndividualServerDocs,
        docId,
        newFilename
      );
      if (nextIndividualServer) self.timelineIndividualServerDocs = nextIndividualServer;

      const nextContentMatches = renameInRows(self.timelineContentMatchDocs, docId, newFilename);
      if (nextContentMatches) self.timelineContentMatchDocs = nextContentMatches;
    },

    /**
     * Apply the server's Verified decision to every in-memory copy of the
     * affected rows, so the badge appears/disappears immediately after a
     * verify/unverify instead of waiting for a refetch.
     *
     * Mirrors `applyRename` and patches the same set of caches — the MST
     * documents map, the folder view's projectDocuments cache, and the
     * timeline's four row lists. Values come from the DB (the trigger owns
     * them), never from the client.
     */
    applyVerified(
      results: Array<{
        id: number;
        verified_at: string | null;
        verified_by: number | null;
        verified_by_name: string | null;
      }>
    ) {
      if (results.length === 0) return;

      const patchById = new Map<string, Partial<DocumentRowMapped>>(
        results.map((r) => [
          String(r.id),
          {
            verified_at: r.verified_at,
            verified_by: r.verified_by,
            verified_by_name: r.verified_by_name,
          },
        ])
      );

      for (const result of results) {
        const model = self.documents.get(String(result.id));
        if (model) {
          model.setVerified(result.verified_at, result.verified_by, result.verified_by_name);
        }
      }

      let patched = false;
      for (const projectCache of self.projectDocuments.values()) {
        for (const [folderKey, docs] of projectCache.entries()) {
          const arr = docs as any[];
          const next = arr.map((d) => {
            const patch = patchById.get(String(d.id));
            return patch ? { ...d, ...patch } : d;
          });
          if (next.some((d, i) => d !== arr[i])) {
            projectCache.set(folderKey, next);
            patched = true;
          }
        }
      }
      if (patched) self.projectCacheVersion += 1;

      for (const cluster of self.expandedClusters.values()) {
        const next = patchRows(cluster.documents, patchById);
        if (next) cluster.setDocumentRows(next);
      }

      const nextIndividual = patchRows(self.timelineIndividualDocs, patchById);
      if (nextIndividual) self.timelineIndividualDocs = nextIndividual;

      const nextIndividualServer = patchRows(self.timelineIndividualServerDocs, patchById);
      if (nextIndividualServer) self.timelineIndividualServerDocs = nextIndividualServer;

      const nextContentMatches = patchRows(self.timelineContentMatchDocs, patchById);
      if (nextContentMatches) self.timelineContentMatchDocs = nextContentMatches;
    },

    // ========================================================================
    // Efficient Query Actions for Documents & Projects Pages
    // ========================================================================

    /**
     * Load available filter values for a building.
     * Returns distinct values that have at least one document.
     * Used to populate filter dropdowns efficiently.
     *
     * @param buildingId - The building ID
     * @param projectId - Optional project ID to scope filters
     */
    loadAvailableFilters: flow(function* loadAvailableFilters(
      buildingId: string,
      projectId?: string
    ) {
      const seq = ++self.filtersRequestSeq;
      // Scope change (another building/project): the old options would resolve
      // against the wrong scope's tag ids, so clear them now instead of
      // showing them until the fetch resolves. Same-scope refetches keep the
      // stale-while-revalidate behaviour below.
      const scopeKey = `${buildingId}:${projectId ?? ""}`;
      if (self.filtersScopeKey !== scopeKey) {
        self.filtersScopeKey = scopeKey;
        self.availableFilters = null;
      }
      self.isLoadingFilters = true;
      self.filtersLoadFailed = false;

      try {
        const filters: AvailableFilters = yield documentsRepo.fetchAvailableFilters(
          buildingId,
          projectId
        );
        // A newer call superseded this one (e.g. rapid project switch) —
        // don't overwrite its scope's values with this stale response.
        if (seq === self.filtersRequestSeq) self.availableFilters = filters;
        return filters;
      } catch (e: any) {
        console.error("loadAvailableFilters error", e);
        if (seq === self.filtersRequestSeq) self.filtersLoadFailed = true;
        // Keep any previously loaded values (stale-while-revalidate): nulling
        // them here would blank the Filters popover on a failed background
        // refetch. A failed FIRST load leaves this null and the popover-open
        // retry in the host page handles it.
        throw e;
      } finally {
        if (seq === self.filtersRequestSeq) self.isLoadingFilters = false;
      }
    }),

    /**
     * Load total document count for a building.
     * Uses efficient COUNT query without fetching document data.
     * Stores result in buildingDocumentCount (not overwritten by loadDocuments).
     *
     * @param buildingId - The building ID
     * @param projectId - Optional project ID to scope the count
     * @returns The total count
     */
    loadDocumentCount: flow(function* loadDocumentCount(buildingId: string, projectId?: string) {
      const seq = ++self.countRequestSeq;
      // Scope change (another building/project): the previous scope's total is
      // not a stale version of this one, it is a different number — drop it now
      // so the views hide the denominator instead of pairing the new filtered
      // count with the old building's total until this COUNT resolves. A
      // same-scope refetch keeps the current value rather than flickering.
      const scopeKey = `${buildingId}:${projectId ?? ""}`;
      if (self.countScopeKey !== scopeKey) {
        self.countScopeKey = scopeKey;
        self.buildingDocumentCount = null;
      }
      try {
        const result: { count: number } = yield documentsRepo.fetchDocumentCount(
          buildingId,
          projectId
        );
        // Store in buildingDocumentCount which won't be overwritten by loadDocuments.
        // Reflects the requested scope (building-wide or a single project).
        // A newer call superseded this one (e.g. rapid project switch) — don't
        // overwrite its scope's total with this stale response.
        if (seq === self.countRequestSeq) self.buildingDocumentCount = result.count;
        return result.count;
      } catch (e: any) {
        // Back to "not counted" so no view renders a denominator from a failed
        // load. Callers fire this and forget, so it is logged, not rethrown —
        // rethrowing only produced an unhandled rejection.
        console.error("loadDocumentCount error", e);
        if (seq === self.countRequestSeq) self.buildingDocumentCount = null;
        return null;
      }
    }),

    /**
     * Load all document filenames for a building (for chatbot auto-detection).
     * Returns a list of filenames for quick lookup/matching.
     *
     * @param buildingId - The building ID
     * @returns Array of document filenames
     */
    loadDocumentFilenames: flow(function* loadDocumentFilenames(buildingId: string) {
      try {
        const filenames: string[] = yield documentsRepo.getDocumentFilenames(buildingId);
        return filenames;
      } catch (e: any) {
        console.error("loadDocumentFilenames error", e);
        // Return empty array on error - auto-detection will just not work
        return [];
      }
    }),

    /**
     * Load document counts for all folders in a project.
     * Returns both direct counts and recursive totals (including nested children).
     *
     * @param projectId - The project ID
     * @param buildingId - The building ID
     * @returns Object with folder counts map and root document count
     */
    loadFolderDocumentCounts(projectId: string, buildingId: string) {
      return fetchFolderDocumentCountsIntoStore(self, projectId, buildingId);
    },

    /**
     * Get the total document count for a specific folder (recursive).
     * Returns 0 if folder counts haven't been loaded yet.
     *
     * @param folderId - The folder ID (null for root)
     * @returns The total document count including nested children
     */
    getFolderTotalCount(folderId: string | null): number {
      if (folderId === null) {
        return self.rootDocumentCount;
      }
      const folderInfo = self.folderDocumentCounts?.get(folderId);
      return folderInfo?.totalCount ?? 0;
    },

    /**
     * Clear folder document counts cache.
     */
    clearFolderCounts() {
      self.folderDocumentCounts = null;
      self.rootDocumentCount = 0;
      self.unreachableDocIds = [];
    },

    // ========================================================================
    // Timeline Actions (Server-Side Aggregation for 6000+ documents)
    // ========================================================================

    /**
     * Load timeline summary (bucketed counts only - no individual docs).
     * Called when entering timeline view or changing granularity.
     *
     * @param buildingId - The building ID
     * @param granularity - Optional granularity (year/month/week/day), defaults to current
     * @param filters - Optional filters (project, categories, disciplines, stages, search, folder)
     */
    loadTimelineSummary: flow(function* loadTimelineSummary(
      buildingId: string,
      granularity?: TimelineGranularity,
      filters?: TimelineFilterParams
    ) {
      const seq = ++self.timelineSummaryRequestSeq;

      self.timelineLoading = true;
      self.timelineError = null;

      const gran = granularity ?? self.timelineGranularity;
      self.timelineGranularity = gran;
      self.timelineDateBasis = filters?.dateBasis ?? "file";

      try {
        const summary: TimelineSummaryResult = yield documentsRepo.fetchTimelineSummary({
          buildingId,
          granularity: gran,
          ...filters,
        });
        if (seq !== self.timelineSummaryRequestSeq) return;
        // Stamp for revalidateVisibleDocuments' freshness check, mirroring
        // loadDocuments' documentsFetchedAt.
        self.timelineFetchedAt = Date.now();
        self.timelineSummaryRaw = summary;
        // Only the raw field is ours; the merged view is rebuilt from every
        // raw input at once so a content search that landed first survives.
        recomputeTimelineMerges(self);
      } catch (e) {
        if (e instanceof TimelineDateBasisUnavailableError) {
          // Not an error to show: the view drops back to the file basis, which
          // re-runs this load without the argument the database lacks.
          self.timelineDateBasisUnavailable = true;
          console.warn("loadTimelineSummary:", e.message);
          return;
        }
        if (seq === self.timelineSummaryRequestSeq) {
          self.timelineError = e instanceof Error ? e.message : String(e);
        }
        console.error("loadTimelineSummary error", e);
      } finally {
        if (seq === self.timelineSummaryRequestSeq) self.timelineLoading = false;
      }
    }),

    /**
     * Fetch document content matches for the timeline view.
     *
     * Mirrors the List view's content search (DocumentStore.loadDocuments) so that
     * documents whose page text matches `searchText` — but whose filename does not —
     * still appear in the Timeline view. Content matches are then merged locally
     * into the bucketed summary, expanded clusters, and max-zoom individual mode.
     *
     * No-ops when neither the search text nor the filters changed since the last
     * successful fetch.
     */
    loadTimelineContentMatches: flow(function* loadTimelineContentMatches(
      buildingId: string,
      searchText: string | undefined,
      filters?: TimelineFilterParams
    ) {
      const trimmed = searchText?.trim() ?? "";

      if (!trimmed) {
        // Always bumped — even with nothing on screen yet, the FIRST search can
        // still be in flight, and it must not land after the box was cleared.
        ++self.timelineContentRequestSeq;
        if (
          self.timelineContentMatchDocs.length === 0 &&
          self.timelineContentMatchSearchText === null
        ) {
          self.timelineContentMatchFilterKey = null;
          self.timelineSearchStatus = COMPLETE_CONTENT_SEARCH;
          return;
        }
        self.timelineContentMatchDocs = [];
        self.timelineContentMatchIds = new Set();
        self.timelineContentMatchInfoById = new Map();
        self.timelineContentMatchSearchText = null;
        self.timelineContentMatchFilterKey = null;
        self.timelineSearchStatus = COMPLETE_CONTENT_SEARCH;
        recomputeTimelineMerges(self);
        return;
      }

      // The content-only rows must pass the same filters as the buckets they
      // are merged into, so a filter change re-runs the search as a text
      // change does.
      const listFilters = {
        buildingId,
        searchText: trimmed,
        projectIds: filters?.projectIds,
        categoryIds: filters?.categoryIds,
        designDisciplineIds: filters?.designDisciplineIds,
        designStageIds: filters?.designStageIds,
        folderId: filters?.folderId,
        verifiedOnly: filters?.verifiedOnly,
        uploadDateRange: filters?.uploadDateRange,
      };
      const filterKey = JSON.stringify(listFilters);

      // Before the bump: a genuine no-op must not cancel the identical request
      // that is already in flight for this exact query. A failed search is
      // never a no-op — asking again is how the user retries it.
      if (
        filterKey === self.timelineContentMatchFilterKey &&
        !self.timelineSearchStatus.contentUnavailable
      ) {
        return;
      }

      const seq = ++self.timelineContentRequestSeq;

      try {
        const result = yield searchFilteredDocumentContent(listFilters);
        const matchInfo: Map<number, ContentMatchInfo> = result.matchInfo;
        const docs: DocumentRowMapped[] = result.documents;

        if (seq !== self.timelineContentRequestSeq) return;

        self.timelineContentMatchDocs = docs;
        self.timelineContentMatchIds = new Set(docs.map((d) => String(d.id)));
        const newTimelineInfoById = new Map<string, ContentMatchInfo>();
        for (const doc of docs) {
          const info = matchInfo.get(doc.id);
          if (info) newTimelineInfoById.set(String(doc.id), info);
        }
        self.timelineContentMatchInfoById = newTimelineInfoById;
        self.timelineContentMatchSearchText = trimmed;
        self.timelineContentMatchFilterKey = filterKey;
        self.timelineSearchStatus = result.status;

        // Rebuilt from every raw input, so a summary that landed first keeps
        // its buckets and a previous search's content-only rows still leave.
        recomputeTimelineMerges(self);
      } catch (e) {
        // Fail-soft: leave timeline working with filename-only results.
        console.warn("loadTimelineContentMatches failed:", e);
        if (seq === self.timelineContentRequestSeq) {
          self.timelineSearchStatus = { isPartial: false, contentUnavailable: true };
        }
      }
    }),

    /**
     * Expand a cluster - fetch first batch of documents.
     * Called when user clicks a cluster to see its documents.
     *
     * @param buildingId - The building ID
     * @param startTime - Start of the time window
     * @param endTime - End of the time window
     * @param filters - Optional filters (project, categories, disciplines, stages, search, folder)
     * @returns Array of documents in the first batch
     */
    expandCluster: flow(function* expandCluster(
      buildingId: string,
      startTime: Date,
      endTime: Date,
      filters?: TimelineFilterParams
    ) {
      const key = `${startTime.toISOString()}|${endTime.toISOString()}`;

      // Create MST model for cluster state (observable)
      const clusterModel = ExpandedClusterModel.create({
        startTimeISO: startTime.toISOString(),
        endTimeISO: endTime.toISOString(),
        documents: [],
        totalCount: 0,
        hasMore: false,
        cursorTimestampISO: null,
        cursorId: null,
        isLoading: true,
      });
      self.expandedClusters.set(key, clusterModel);

      try {
        const result: documentsRepo.TimelineWindowResult =
          yield documentsRepo.fetchDocumentsInTimeWindow({
            buildingId,
            startTime,
            endTime,
            limit: 50, // First batch
            ...filters,
          });

        // Merge in content-match docs whose timestamp falls in this cluster's window
        // and weren't already returned by the filename-search RPC.
        const merged = mergeContentMatchesIntoWindow(
          self.timelineContentMatchDocs,
          result,
          startTime,
          endTime,
          filters?.dateBasis ?? "file"
        );

        // IMPORTANT: After async fetch, the cluster may have been removed from the tree
        // (e.g., granularity change cleared expandedClusters). Get current model from map.
        const currentModel = self.expandedClusters.get(key);
        if (currentModel && currentModel === clusterModel) {
          // Same model still exists - safe to update
          currentModel.setDocuments(
            merged.documents,
            merged.totalCount,
            result.hasMore,
            result.cursor,
            merged.splicedIds
          );
        }
        // If model was removed/replaced, silently ignore - new expansion will handle it

        return merged.documents;
      } catch (e) {
        // Only update if model still exists in tree
        const currentModel = self.expandedClusters.get(key);
        if (currentModel && currentModel === clusterModel) {
          currentModel.setLoading(false);
        }
        self.timelineError = e instanceof Error ? e.message : String(e);
        console.error("expandCluster error", e);
        throw e;
      }
    }),

    /**
     * Load more documents in an expanded cluster (lazy load on scroll).
     * Returns false if no more to load.
     *
     * @param buildingId - The building ID
     * @param startTime - Start of the time window
     * @param endTime - End of the time window
     * @param filters - Optional filters (project, categories, disciplines, stages, search, folder)
     * @returns true if more docs were loaded, false if none left
     */
    loadMoreInCluster: flow(function* loadMoreInCluster(
      buildingId: string,
      startTime: Date,
      endTime: Date,
      filters?: TimelineFilterParams
    ) {
      const key = `${startTime.toISOString()}|${endTime.toISOString()}`;
      const clusterModel = self.expandedClusters.get(key);

      // `truncated` is a window whose paging gave up, not one that ran out:
      // hasMore is false, but the rest of it is still there to be re-read.
      if (
        !clusterModel ||
        (!clusterModel.hasMore && !clusterModel.truncated) ||
        clusterModel.isLoading
      ) {
        return false; // Nothing to load or already loading
      }

      // A truncated window's cursor is the one the server rejected. Drop it and
      // read the window again from the start, back to the depth we hold — the
      // same recovery a stale cursor gets, so the result replaces the rows
      // rather than being appended to them.
      const retryingTruncated = clusterModel.truncated;
      const cursorForFetch = retryingTruncated ? undefined : (clusterModel.cursor ?? undefined);
      clusterModel.setLoading(true);

      try {
        const result: documentsRepo.TimelineWindowResult =
          yield documentsRepo.fetchDocumentsInTimeWindow({
            buildingId,
            startTime,
            endTime,
            limit: 50,
            cursor: cursorForFetch,
            // If the cursor turns out to be stale, the repository re-reads this
            // window from the start; tell it how deep the user is scrolled so a
            // 200-row cluster comes back 200 rows and not 50. The depth it pages
            // to is the SERVER's, so the client-spliced content matches come off
            // the count — they are not rows the RPC will hand back, and counting
            // them asks the window for rows past the ones we actually held.
            loadedRows: clusterModel.documents.length - clusterModel.splicedContentIds.length,
            ...filters,
          });

        // IMPORTANT: After async fetch, verify model still exists and is same instance
        const currentModel = self.expandedClusters.get(key);
        if (currentModel && currentModel === clusterModel) {
          if (result.restarted || retryingTruncated) {
            // Our cursor named a row that has left the window, so the repository
            // re-read it from the start, as deep as we had it. Those are rows we
            // already hold — replace, or every one of them renders twice. What
            // comes back is the server's answer alone, so the content-match hits
            // this cluster was expanded with have to be spliced back in exactly
            // as the expansion did it; appending them instead of re-merging
            // would drop them and shrink the count.
            const restarted = mergeContentMatchesIntoWindow(
              self.timelineContentMatchDocs,
              result,
              startTime,
              endTime,
              filters?.dateBasis ?? "file"
            );
            currentModel.setDocuments(
              restarted.documents,
              restarted.totalCount,
              result.hasMore,
              result.cursor,
              restarted.splicedIds,
              result.truncated ?? false
            );
          } else {
            // Same model still exists - safe to update. Not a bare append:
            // page one carries the spliced-in content matches, so this page has
            // to be deduped and re-sorted against it (see appendPageIntoWindow).
            const appended = appendPageIntoWindow(
              currentModel.documents,
              result.documents,
              currentModel.totalCount,
              currentModel.splicedContentIds,
              filters?.dateBasis ?? "file"
            );
            currentModel.setDocuments(
              appended.documents,
              appended.totalCount,
              result.hasMore,
              result.cursor,
              appended.splicedIds,
              result.truncated ?? false
            );
          }
        }
        // If model was removed/replaced, silently ignore

        return true;
      } catch (e) {
        // Only update if model still exists in tree
        const currentModel = self.expandedClusters.get(key);
        if (currentModel && currentModel === clusterModel) {
          currentModel.setLoading(false);
        }
        self.timelineError = e instanceof Error ? e.message : String(e);
        console.error("loadMoreInCluster error", e);
        throw e;
      }
    }),

    /**
     * Collapse a cluster (remove from expanded state).
     *
     * @param startTime - Start of the time window
     * @param endTime - End of the time window
     */
    collapseCluster(startTime: Date, endTime: Date) {
      const key = `${startTime.toISOString()}|${endTime.toISOString()}`;
      // The rows leave the union, the selection does not: every bulk action
      // recovers ids no surface renders (`resolveSelectionForAction`), exactly
      // as it does for a selection made on a list page the user has left.
      self.expandedClusters.delete(key);
    },

    /**
     * Update granularity based on zoom level.
     * Collapses all expanded clusters when granularity changes.
     *
     * @param zoomLevel - Current zoom level (0-100)
     * @param buildingId - The building ID
     * @param filters - Optional filters (project, categories, disciplines, stages, search, folder)
     */
    updateTimelineGranularity(
      zoomLevel: number,
      buildingId: string,
      filters?: TimelineFilterParams
    ) {
      const newGranularity = documentsRepo.zoomToGranularity(zoomLevel);
      if (newGranularity !== self.timelineGranularity) {
        // Collapse all clusters when zooming changes granularity. The selection
        // survives the cache drop — see collapseCluster.
        self.expandedClusters.clear();
        // Re-fetch summary with new granularity
        this.loadTimelineSummary(buildingId, newGranularity, filters);
      }
    },

    /**
     * Clear all timeline state (when switching buildings or exiting timeline view).
     */
    clearTimelineCache() {
      // A generation boundary: useServerTimelineZoom calls this from an effect
      // CLEANUP, so a request issued for the filters being left is still in
      // flight. Without the bumps it lands after the clear and repopulates the
      // cache that was just emptied, with the old filters' data.
      ++self.timelineSummaryRequestSeq;
      ++self.timelineContentRequestSeq;
      ++self.timelineIndividualRequestSeq;

      self.timelineSummary = null;
      self.timelineSummaryRaw = null;
      self.expandedClusters.clear();
      self.timelineError = null;
      // Also clear individual docs mode state
      self.timelineIndividualDocs = [];
      self.timelineIndividualServerDocs = [];
      self.timelineIndividualServerTotalCount = 0;
      self.timelineIndividualTotalCount = 0;
      self.timelineIndividualHasMore = false;
      self.timelineIndividualTruncated = false;
      self.timelineIndividualCursor = null;
      // Clear timeline content-match cache too
      self.timelineContentMatchDocs = [];
      self.timelineContentMatchIds = new Set();
      self.timelineContentMatchInfoById = new Map();
      self.timelineContentMatchSearchText = null;
      self.timelineContentMatchFilterKey = null;
      self.timelineSearchStatus = COMPLETE_CONTENT_SEARCH;
    },

    // ========================================================================
    // Timeline Individual Docs Mode Actions (Max Zoom)
    // ========================================================================

    /**
     * Load individual documents for max zoom mode.
     * Fetches ALL documents with pagination (not bucketed by time).
     * Documents are sorted by timestamp DESC (newest first).
     *
     * @param buildingId - The building ID
     * @param reset - If true, resets state and starts from first page
     * @param filters - Optional filters (project, categories, disciplines, stages, search, folder)
     */
    loadTimelineIndividualDocs: flow(function* loadTimelineIndividualDocs(
      buildingId: string,
      reset = false,
      filters?: TimelineFilterParams
    ) {
      // A reset replaces the list and claims a new generation; a load-more only
      // appends, so it observes the generation instead — same asymmetry as
      // loadDocuments / loadMoreDocuments. A load-more that comes back
      // `restarted` has replaced the list after all, and claims one below.
      let seq: number;

      // If reset, clear existing docs
      if (reset) {
        seq = ++self.timelineIndividualRequestSeq;
        self.timelineDateBasis = filters?.dateBasis ?? "file";
        self.timelineIndividualDocs = [];
        self.timelineIndividualServerDocs = [];
        self.timelineIndividualServerTotalCount = 0;
        self.timelineIndividualCursor = null;
        self.timelineIndividualLoading = true;
      } else {
        // Loading more - check if we have more to load
        if (
          !self.timelineIndividualHasMore &&
          !self.timelineIndividualTruncated &&
          self.timelineIndividualServerDocs.length > 0
        ) {
          return false;
        }
        seq = self.timelineIndividualRequestSeq;
        self.timelineIndividualLoadingMore = true;
      }

      // A truncated list is one whose paging gave up, not one that ran out: the
      // cursor it holds is the one the server rejected, so the retry drops it
      // and re-reads from the start, back to the depth already on screen.
      const retryingTruncated = !reset && self.timelineIndividualTruncated;

      try {
        const result: documentsRepo.TimelineWindowResult =
          yield documentsRepo.fetchAllTimelineDocuments(
            buildingId,
            50, // Load 50 docs at a time
            retryingTruncated ? undefined : (self.timelineIndividualCursor ?? undefined),
            filters,
            // Depth to restore if this cursor is rejected as stale, or if we are
            // re-reading a truncated list — otherwise either replaces every
            // loaded page with the first 50.
            self.timelineIndividualServerDocs.length
          );

        // Superseded by a newer reset (a filter change, a cache clear).
        if (seq !== self.timelineIndividualRequestSeq) return false;

        // `restarted` is the repository having thrown our stale cursor away and
        // re-read the window from the start, back to the depth we had — same as
        // a reset as far as the list is concerned, and appending it would
        // re-serve rows we already have.
        if (reset || result.restarted || retryingTruncated) {
          // A restart replaces the list, so — exactly like a reset — it claims a
          // new generation. Without the bump, a page request issued against the
          // pre-restart cursor and still in flight lands afterwards, appends its
          // rows onto the list that was just replaced, and leaves both a hole
          // where page two should be and a cursor pointing past it.
          if (result.restarted || retryingTruncated) seq = ++self.timelineIndividualRequestSeq;
          self.timelineIndividualServerDocs = result.documents;
        } else {
          // Subsequent pages: dedupe against what we've already fetched server-side.
          const seen = new Set(self.timelineIndividualServerDocs.map((d) => d.id));
          const fresh = result.documents.filter((d) => !seen.has(d.id));
          self.timelineIndividualServerDocs = [...self.timelineIndividualServerDocs, ...fresh];
        }

        self.timelineIndividualServerTotalCount = result.totalCount;
        // Re-merge with content-match docs (idempotent — handles late-arriving content fetch).
        recomputeTimelineMerges(self);
        self.timelineIndividualHasMore = result.hasMore;
        self.timelineIndividualTruncated = result.truncated ?? false;
        self.timelineIndividualCursor = result.cursor;

        return true;
      } catch (e) {
        if (e instanceof TimelineDateBasisUnavailableError) {
          // See loadTimelineSummary: the view falls back to the file basis.
          self.timelineDateBasisUnavailable = true;
          console.warn("loadTimelineIndividualDocs:", e.message);
          return false;
        }
        if (seq === self.timelineIndividualRequestSeq) {
          self.timelineError = e instanceof Error ? e.message : String(e);
        }
        console.error("loadTimelineIndividualDocs error", e);
        throw e;
      } finally {
        if (seq === self.timelineIndividualRequestSeq) {
          self.timelineIndividualLoading = false;
          self.timelineIndividualLoadingMore = false;
        }
      }
    }),

    /**
     * Clear individual docs mode state (when exiting max zoom).
     */
    clearTimelineIndividualDocs() {
      // Called on every exit from max zoom (useServerTimelineZoom). The rows go,
      // the selection stays: a bulk action recovers ids no surface renders (see
      // collapseCluster), so zooming out to check context no longer throws away
      // what the user had checked.
      ++self.timelineIndividualRequestSeq;
      self.timelineIndividualDocs = [];
      self.timelineIndividualServerDocs = [];
      self.timelineIndividualServerTotalCount = 0;
      self.timelineIndividualTotalCount = 0;
      self.timelineIndividualHasMore = false;
      self.timelineIndividualTruncated = false;
      self.timelineIndividualCursor = null;
    },
  }));

export type DocumentStoreInstance = Instance<typeof DocumentStore>;
export type DocumentStoreSnapshotIn = SnapshotIn<typeof DocumentStore>;
export type DocumentStoreSnapshotOut = SnapshotOut<typeof DocumentStore>;
